/**
 * FikoDFMAnalyzer - Design for Manufacturing Analysis System
 *
 * BREP analiz sonuçlarını kullanarak kapsamlı DFM raporu üretir.
 * Üretim öncesi tasarım kontrolü için profesyonel analiz sistemi.
 */

import * as THREE from 'three';
import {
    FikoGPUBrepExtractor,
    GrupBrepResult,
    ManufacturingAnalysis,
    WallThicknessInfo,
    DraftAngleInfo,
    UndercutInfo,
    HoleInfo,
    FilletInfo,
    ChamferInfo,
    RibInfo,
    ThreadInfo,
    PartingLineInfo
} from './FikoGPUBrepExtractor';
import { FikoCostEngine, getCostEngine, CostEstimationInput, CostEstimationResult, PartGeometry, DetailedCostBreakdown, ProcessCostComparison } from './FikoCostEngine';
import { getMaterialDB, MaterialProperties as MatProps, ManufacturingProcessType } from './FikoMaterialDB';

// ============================================================
// DFM ANALYSIS TYPES
// ============================================================

export type DFMSeverity = 'critical' | 'warning' | 'info' | 'pass';
export type ManufacturingProcess =
    | 'injection_molding' | 'die_casting' | 'cnc_machining' | 'sheet_metal' | '3d_printing' | 'forging'
    // Yeni surecler
    | 'sand_casting' | 'investment_casting' | 'shell_mold_casting' | 'centrifugal_casting'
    | 'cnc_turning' | 'cnc_5axis' | 'cnc_edm' | 'cnc_grinding'
    | 'welding' | 'heat_treatment' | 'surface_treatment';

export interface DFMIssue {
    id: string;
    severity: DFMSeverity;
    category: string;
    title: string;
    description: string;
    affectedFeatures: string[];  // Face IDs, Edge IDs, etc.
    recommendation: string;
    estimatedCostImpact?: number;  // Percentage cost increase
    relatedStandard?: string;      // ISO, DIN, etc.
    /** Otomatik duzeltme onerisi uygulanabilir mi */
    autofix?: boolean;
    /** Somut tasarim onerisi (ornegin "Duvar kalinligini 1.2mm'den 2.0mm'ye artirin") */
    designSuggestion?: string;
}

export interface DFMScore {
    overall: number;           // 0-100
    /** Proses-geometri uyumluluk skoru (en yuksek agirlik) */
    processGeometryFit: number;
    wallThickness: number;
    draftAngles: number;
    undercuts: number;
    holes: number;
    fillets: number;
    ribs: number;
    threads: number;
    partingLine: number;
    symmetry: number;
    // Yeni kategoriler
    pockets: number;           // Cepler/kaviteler
    bosses: number;            // Çıkıntılar/patronlar
    thinSections: number;      // İnce kesitler
    internalCorners: number;   // İç köşeler
    sinkMarks: number;         // Çökme izi riski
    snapFits: number;          // Klips bağlantıları
    surfaceFinish: number;     // Yüzey kalitesi
    // Web araştırmasından eklenen kategoriler
    airTraps: number;          // Hava tuzakları
    weldLines: number;         // Kaynak çizgileri
    warpage: number;           // Çarpılma riski
    tolerances: number;        // Tolerans uyumu
    ejectorPins: number;       // Çıkarıcı pin yerleşimi
}

/** DFM maliyet parametreleri (UI'dan gelen ayarlar) */
export interface DFMCostParams {
    batchSize?: number;
    annualVolume?: number;
    profitMarginPercent?: number;
    overheadPercent?: number;
    qualityControlPercent?: number;
    tightestToleranceMM?: number;
    requiredSurfaceRa?: number;
    materialId?: string;
    outputCurrency?: 'USD' | 'EUR' | 'TRY';
    customCostItems?: { id: string; name: string; costPerPart: number; category: string; enabled: boolean }[];
}

export interface CostEstimate {
    baselineCost: number;           // Optimum tasarım maliyeti
    currentCost: number;            // Mevcut tasarım maliyeti
    potentialSavings: number;       // Düzeltme ile tasarruf
    savingsPercentage: number;
    costBreakdown: {
        tooling: number;
        material: number;
        machining: number;
        assembly: number;
        qualityControl: number;
    };

    // ── Gercek fizik-bazli maliyet motoru sonuclari ──
    /** Detayli maliyet hesaplama sonucu (FikoCostEngine) */
    detailedResult?: CostEstimationResult;
    /** Surec karsilastirma tablosu */
    processComparisons?: ProcessCostComparison[];
    /** Parca agirligi (kg) */
    partWeightKg?: number;
    /** Parca basina en iyi maliyet */
    bestCostPerPart?: number;
    /** kg basina en iyi maliyet */
    bestCostPerKg?: number;
    /** Onerilen uretim sureci */
    recommendedProcess?: string;
    recommendedProcessTR?: string;
    /** Fizibilite sinyali */
    feasibility?: 'green' | 'yellow' | 'red';
    /** Para birimi */
    currency?: string;
    /** Custom items toplami */
    customItemsTotal?: number;
    /** Grand total (detailedResult + custom items) */
    grandTotal?: number;
}

export interface DFMReportSection {
    title: string;
    score: number;
    issues: DFMIssue[];
    recommendations: string[];
}

export interface DFMReport {
    // Metadata
    reportId: string;
    partName: string;
    generatedAt: Date;
    analysisVersion: string;

    // Manufacturing Process
    targetProcess: ManufacturingProcess;
    material?: string;

    // Overall Results
    overallScore: number;
    manufacturabilityRating: 'excellent' | 'good' | 'acceptable' | 'poor' | 'unmanufacturable';

    // Detailed Scores
    scores: DFMScore;

    // Issues Summary
    criticalIssues: DFMIssue[];
    warnings: DFMIssue[];
    suggestions: DFMIssue[];
    passed: DFMIssue[];

    // Sections
    sections: DFMReportSection[];

    // Cost Analysis
    costEstimate: CostEstimate;

    // Recommendations
    topRecommendations: string[];

    // Raw Data
    brepAnalysis?: GrupBrepResult;
    manufacturingAnalysis?: ManufacturingAnalysis;
}

// ============================================================
// DFM RULES & THRESHOLDS
// ============================================================

interface ProcessRules {
    minWallThickness: number;
    maxWallThickness: number;
    minDraftAngle: number;
    recommendedDraftAngle: number;
    maxHoleDepthRatio: number;
    minFilletRadius: number;
    maxUndercutDepth: number;
    allowsUndercuts: boolean;
    // ── Sprint 1.2: Genisletilmis proses parametreleri ──
    /** Minimum ozellik boyutu (mm) */
    minFeatureSize?: number;
    /** Maksimum en-boy orani (yukseklik/genislik) */
    maxAspectRatio?: number;
    /** Onerien yaricaplar (mm) */
    recommendedRadii?: { internal: number; external: number };
    /** Uygun yolluk tipleri (injection icin) */
    gateTypes?: string[];
    /** Cikarma gereksinimleri */
    ejectionRequirements?: { minDraft: number; maxForce: number };
    /** Minimum ozellik mesafesi (mm) - delikler arasi, nervur-duvar mesafesi */
    minFeatureSpacing?: number;
    /** Minimum ic kose yaricapi (mm) */
    minInternalCornerRadius?: number;
    /** Maksimum L/D orani (uzunluk/cap) */
    maxLDRatio?: number;
}

const PROCESS_RULES: Record<ManufacturingProcess, ProcessRules> = {
    injection_molding: {
        minWallThickness: 0.8,      // mm
        maxWallThickness: 6.0,
        minDraftAngle: 0.5,         // degrees
        recommendedDraftAngle: 2.0,
        maxHoleDepthRatio: 4,
        minFilletRadius: 0.5,
        maxUndercutDepth: 5,
        allowsUndercuts: true,       // Side actions ile
        minFeatureSize: 0.5,
        maxAspectRatio: 4,
        recommendedRadii: { internal: 0.5, external: 1.0 },
        gateTypes: ['edge', 'submarine', 'pin_point', 'fan', 'tab', 'hot_runner'],
        ejectionRequirements: { minDraft: 0.5, maxForce: 50 },
        minFeatureSpacing: 2.0,
        minInternalCornerRadius: 0.5,
        maxLDRatio: 8,
    },
    die_casting: {
        minWallThickness: 1.0,
        maxWallThickness: 12.0,
        minDraftAngle: 1.0,
        recommendedDraftAngle: 3.0,
        maxHoleDepthRatio: 3,
        minFilletRadius: 1.0,
        maxUndercutDepth: 3,
        allowsUndercuts: false,
        minFeatureSize: 1.0,
        maxAspectRatio: 5,
        recommendedRadii: { internal: 1.0, external: 2.0 },
        ejectionRequirements: { minDraft: 1.0, maxForce: 80 },
        minFeatureSpacing: 3.0,
        minInternalCornerRadius: 1.0,
        maxLDRatio: 6,
    },
    cnc_machining: {
        minWallThickness: 0.5,
        maxWallThickness: 100,
        minDraftAngle: 0,           // Gerekli değil
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 10,
        minFilletRadius: 0.1,
        maxUndercutDepth: 50,
        allowsUndercuts: true,       // 5-axis ile
        minFeatureSize: 0.5,
        maxAspectRatio: 8,
        recommendedRadii: { internal: 0.5, external: 0.5 },
        minFeatureSpacing: 1.5,
        minInternalCornerRadius: 0.5,
        maxLDRatio: 12,
    },
    sheet_metal: {
        minWallThickness: 0.5,
        maxWallThickness: 6.0,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 1,       // Punch için
        minFilletRadius: 0.5,       // Bend radius
        maxUndercutDepth: 0,
        allowsUndercuts: false,
        minFeatureSize: 1.0,
        maxAspectRatio: 10,
        recommendedRadii: { internal: 0.5, external: 1.0 },
        minFeatureSpacing: 2.0,
        minInternalCornerRadius: 0.5,
    },
    '3d_printing': {
        minWallThickness: 0.4,      // FDM için
        maxWallThickness: 50,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 20,
        minFilletRadius: 0,
        maxUndercutDepth: 100,      // Support ile
        allowsUndercuts: true,
        minFeatureSize: 0.4,
        maxAspectRatio: 8,
        minFeatureSpacing: 0.8,
        minInternalCornerRadius: 0,
    },
    forging: {
        minWallThickness: 3.0,
        maxWallThickness: 100,
        minDraftAngle: 3.0,
        recommendedDraftAngle: 7.0,
        maxHoleDepthRatio: 2,
        minFilletRadius: 3.0,
        maxUndercutDepth: 0,
        allowsUndercuts: false,
        minFeatureSize: 3.0,
        maxAspectRatio: 6,
        recommendedRadii: { internal: 3.0, external: 5.0 },
        ejectionRequirements: { minDraft: 3.0, maxForce: 200 },
        minFeatureSpacing: 5.0,
        minInternalCornerRadius: 3.0,
        maxLDRatio: 4,
    },

    // ═══════════════════════════════════════════════════════════
    // YENİ SÜREÇLER - Casting Variants
    // ═══════════════════════════════════════════════════════════
    sand_casting: {
        minWallThickness: 3.0,       // Kum döküm daha kalın duvar gerektirir
        maxWallThickness: 200,
        minDraftAngle: 1.5,          // 1.5-3° pattern çıkışı için
        recommendedDraftAngle: 3.0,
        maxHoleDepthRatio: 2,        // Core ile delik sınırlı
        minFilletRadius: 3.0,        // Kum döküm keskin köşe istemez
        maxUndercutDepth: 10,        // Core ile mümkün ama sınırlı
        allowsUndercuts: true,        // Çekirdek (core) ile
        minFeatureSize: 5.0,
        maxAspectRatio: 4,
        recommendedRadii: { internal: 3.0, external: 5.0 },
        ejectionRequirements: { minDraft: 1.5, maxForce: 100 },
        minFeatureSpacing: 8.0,
        minInternalCornerRadius: 3.0,
        maxLDRatio: 4,
    },
    investment_casting: {
        minWallThickness: 0.5,       // Hassas döküm ince duvar yapabilir
        maxWallThickness: 75,
        minDraftAngle: 0,            // Kayıp kalıp - draft gerekmez
        recommendedDraftAngle: 0.5,  // Yine de önerilir
        maxHoleDepthRatio: 6,
        minFilletRadius: 0.3,
        maxUndercutDepth: 20,        // Wax pattern eridiği için alttan kesim mümkün
        allowsUndercuts: true,
        minFeatureSize: 0.5,
        maxAspectRatio: 6,
        recommendedRadii: { internal: 0.3, external: 0.5 },
        minFeatureSpacing: 2.0,
        minInternalCornerRadius: 0.3,
        maxLDRatio: 8,
    },
    shell_mold_casting: {
        minWallThickness: 2.0,
        maxWallThickness: 50,
        minDraftAngle: 1.0,
        recommendedDraftAngle: 2.0,
        maxHoleDepthRatio: 3,
        minFilletRadius: 1.5,
        maxUndercutDepth: 5,
        allowsUndercuts: true,        // Core ile sınırlı
        minFeatureSize: 2.0,
        maxAspectRatio: 5,
        recommendedRadii: { internal: 1.5, external: 2.5 },
        minFeatureSpacing: 4.0,
        minInternalCornerRadius: 1.5,
        maxLDRatio: 5,
    },
    centrifugal_casting: {
        minWallThickness: 2.5,
        maxWallThickness: 150,
        minDraftAngle: 0,            // Silindirik parçalar - draft yok
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 1,        // İç çap santrifüj ile oluşur
        minFilletRadius: 2.0,
        maxUndercutDepth: 0,
        allowsUndercuts: false,       // Sadece simetrik/silindirik parçalar
        minFeatureSize: 3.0,
        maxAspectRatio: 10,
        recommendedRadii: { internal: 2.0, external: 3.0 },
        minFeatureSpacing: 5.0,
        minInternalCornerRadius: 2.0,
    },

    // ═══════════════════════════════════════════════════════════
    // YENİ SÜREÇLER - CNC Variants
    // ═══════════════════════════════════════════════════════════
    cnc_turning: {
        minWallThickness: 0.5,
        maxWallThickness: 100,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 8,        // Derin delme mümkün
        minFilletRadius: 0.1,
        maxUndercutDepth: 10,        // İç tırnak ile sınırlı
        allowsUndercuts: true,        // İç/dış tırnak aleti ile
        minFeatureSize: 0.3,
        maxAspectRatio: 10,
        recommendedRadii: { internal: 0.2, external: 0.3 },
        minFeatureSpacing: 1.0,
        minInternalCornerRadius: 0.2,
        maxLDRatio: 10,
    },
    cnc_5axis: {
        minWallThickness: 0.3,       // 5 eksen çok ince duvar yapabilir
        maxWallThickness: 200,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 15,       // Açılı yaklaşım ile derin delik
        minFilletRadius: 0.05,       // Küçük takım ile çok ince fillet
        maxUndercutDepth: 100,       // Tam erişim
        allowsUndercuts: true,
        minFeatureSize: 0.2,
        maxAspectRatio: 12,
        recommendedRadii: { internal: 0.1, external: 0.2 },
        minFeatureSpacing: 0.5,
        minInternalCornerRadius: 0.1,
        maxLDRatio: 15,
    },
    cnc_edm: {
        minWallThickness: 0.1,       // Tel erozyon çok ince kesebilir
        maxWallThickness: 300,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 20,       // Dalma erozyon derin delik yapabilir
        minFilletRadius: 0.02,       // Çok keskin iç köşeler mümkün
        maxUndercutDepth: 50,
        allowsUndercuts: true,
        minFeatureSize: 0.05,
        maxAspectRatio: 20,
        recommendedRadii: { internal: 0.02, external: 0.05 },
        minFeatureSpacing: 0.3,
        minInternalCornerRadius: 0.02,
        maxLDRatio: 20,
    },
    cnc_grinding: {
        minWallThickness: 0.5,
        maxWallThickness: 50,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 5,
        minFilletRadius: 0.05,
        maxUndercutDepth: 5,         // Profil taşlama ile sınırlı
        allowsUndercuts: true,
        minFeatureSize: 0.3,
        maxAspectRatio: 8,
        recommendedRadii: { internal: 0.05, external: 0.1 },
        minFeatureSpacing: 1.0,
        minInternalCornerRadius: 0.05,
    },

    // ═══════════════════════════════════════════════════════════
    // YENİ SÜREÇLER - Post-Processing
    // ═══════════════════════════════════════════════════════════
    welding: {
        minWallThickness: 0.8,       // Kaynak için minimum et kalınlığı
        maxWallThickness: 200,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 0,        // Kaynak delik yapmaz
        minFilletRadius: 1.0,        // Kaynak dikişi doğal fillet yapar
        maxUndercutDepth: 0,
        allowsUndercuts: false
    },
    heat_treatment: {
        minWallThickness: 1.0,       // Isıl işlem çok ince kesimlerde çarpılma yapar
        maxWallThickness: 500,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 0,
        minFilletRadius: 0,
        maxUndercutDepth: 0,
        allowsUndercuts: true        // Geometri değişmez
    },
    surface_treatment: {
        minWallThickness: 0.5,
        maxWallThickness: 500,
        minDraftAngle: 0,
        recommendedDraftAngle: 0,
        maxHoleDepthRatio: 0,
        minFilletRadius: 0,
        maxUndercutDepth: 0,
        allowsUndercuts: true        // Kaplama ulaşabilirse OK
    }
};

// ============================================================
// MAIN DFM ANALYZER CLASS
// ============================================================

export class FikoDFMAnalyzer {
    private brepExtractor: FikoGPUBrepExtractor;
    private targetProcess: ManufacturingProcess = 'injection_molding';
    private rules: ProcessRules;

    constructor() {
        this.brepExtractor = new FikoGPUBrepExtractor();
        this.rules = PROCESS_RULES[this.targetProcess];
    }

    /**
     * Set target manufacturing process
     */
    setProcess(process: ManufacturingProcess): void {
        this.targetProcess = process;
        this.rules = PROCESS_RULES[process];
    }

    /**
     * Run full DFM analysis
     * @param brepResult - BREP analysis result from FikoGPUBrepExtractor
     * @param partName - Name of the part for the report
     * @param material - Optional material specification
     * @param mesh - Optional mesh for additional manufacturing analysis
     */
    async analyze(
        brepResult: GrupBrepResult,
        partName: string = 'Unnamed Part',
        material?: string,
        mesh?: THREE.Mesh,
        costParams?: DFMCostParams
    ): Promise<DFMReport> {
        // 1. Manufacturing analysis (BREP result already extracted)
        const mfgAnalysis = this.brepExtractor.performManufacturingAnalysis(brepResult, mesh, {
            analyzeSlots: true,
            analyzeGrooves: true,
            analyzeSteps: true,
            analyzeCurvature: true,
            computeMass: true,
            analyzeDovetails: true,
            analyzeFlanges: true,
            analyzeKeyways: true,
            analyzeCounterbores: true,
            analyzeKnurls: true,
            analyzePartingLine: true
        });

        // 3. Generate issues
        const allIssues = this.generateIssues(brepResult, mfgAnalysis);

        // 4. Calculate scores
        const scores = this.calculateScores(brepResult, mfgAnalysis, allIssues);

        // 5. Cost estimation (with BREP manufacturing data + user params)
        const costEstimate = this.estimateCost(allIssues, brepResult, mfgAnalysis, costParams);

        // 6. Generate sections
        const sections = this.generateSections(allIssues);

        // 7. Top recommendations
        const topRecommendations = this.generateTopRecommendations(allIssues);

        // 8. Build report
        const report: DFMReport = {
            reportId: `DFM-${Date.now().toString(36).toUpperCase()}`,
            partName,
            generatedAt: new Date(),
            analysisVersion: '2.0.0',
            targetProcess: this.targetProcess,
            material,

            overallScore: scores.overall,
            manufacturabilityRating: this.getManufacturabilityRating(scores.overall),

            scores,

            criticalIssues: allIssues.filter(i => i.severity === 'critical'),
            warnings: allIssues.filter(i => i.severity === 'warning'),
            suggestions: allIssues.filter(i => i.severity === 'info'),
            passed: allIssues.filter(i => i.severity === 'pass'),

            sections,
            costEstimate,
            topRecommendations,

            brepAnalysis: brepResult,
            manufacturingAnalysis: mfgAnalysis
        };

        return report;
    }

    // ============================================================
    // ISSUE GENERATION
    // ============================================================

    private generateIssues(
        brep: GrupBrepResult,
        mfg: ManufacturingAnalysis
    ): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let issueId = 1;

        // ════════════════════════════════════════════════════════
        // EN KRITIK: Proses-Geometri Uyumluluk Kontrolu
        // Bu kontrol tum diger kontrollerden ONCE yapilmali
        // ════════════════════════════════════════════════════════
        issues.push(...this.analyzeProcessGeometryFitness(brep, mfg, issueId));
        issueId += 20;

        // Wall Thickness Issues
        if (mfg.wallThickness) {
            issues.push(...this.analyzeWallThickness(mfg.wallThickness, issueId));
            issueId += 10;
        }

        // Draft Angle Issues
        if (mfg.draftAngles) {
            issues.push(...this.analyzeDraftAngles(mfg.draftAngles, issueId));
            issueId += 10;
        }

        // Undercut Issues
        if (mfg.undercuts) {
            issues.push(...this.analyzeUndercuts(mfg.undercuts, issueId));
            issueId += 10;
        }

        // Hole Issues
        if (brep.features?.holes) {
            issues.push(...this.analyzeHoles(brep.features.holes, issueId));
            issueId += 10;
        }

        // Fillet Issues
        if (brep.features?.fillets) {
            issues.push(...this.analyzeFillets(brep.features.fillets, issueId));
            issueId += 10;
        }

        // Rib Issues
        if (brep.features?.ribs) {
            issues.push(...this.analyzeRibs(brep.features.ribs, issueId));
            issueId += 10;
        }

        // Thread Issues
        if (mfg.threads) {
            issues.push(...this.analyzeThreads(mfg.threads, issueId));
            issueId += 10;
        }

        // Parting Line Issues
        if (mfg.partingLine) {
            issues.push(...this.analyzePartingLine(mfg.partingLine, issueId));
            issueId += 10;
        }

        // Sharp Edge Issues (no fillets/chamfers)
        issues.push(...this.analyzeSharpEdges(brep, issueId));
        issueId += 10;

        // ============ YENİ ANALİZLER ============

        // Pocket/Cavity Issues (Cepler)
        if (brep.features?.pockets) {
            issues.push(...this.analyzePockets(brep.features.pockets, issueId));
            issueId += 10;
        }

        // Boss Issues (Çıkıntılar/Patronlar)
        if (brep.features?.bosses) {
            issues.push(...this.analyzeBosses(brep.features.bosses, issueId));
            issueId += 10;
        }

        // Thin Section Issues (İnce Kesitler)
        issues.push(...this.analyzeThinSections(brep, issueId));
        issueId += 10;

        // Internal Corner Issues (İç Köşeler)
        issues.push(...this.analyzeInternalCorners(brep, issueId));
        issueId += 10;

        // Sink Mark Risk Analysis (Çökme İzi Riski)
        issues.push(...this.analyzeSinkMarks(brep, mfg, issueId));
        issueId += 10;

        // Snap Fit Analysis (Klips Bağlantıları) - Gelecekte features.snapFits eklendiğinde aktif edilecek
        // if ((brep.features as any)?.snapFits) {
        //     issues.push(...this.analyzeSnapFits((brep.features as any).snapFits, issueId));
        //     issueId += 10;
        // }

        // Surface Finish Analysis (Yüzey Kalitesi)
        issues.push(...this.analyzeSurfaceFinish(brep, issueId));
        issueId += 10;

        // ============ WEB ARAŞTIRMASINDAN GELEN EK KONTROLLER ============

        // Air Traps (Hava Tuzakları) - Enjeksiyon kalıplama
        issues.push(...this.analyzeAirTraps(brep, mfg, issueId));
        issueId += 10;

        // Weld Lines (Kaynak Çizgileri)
        issues.push(...this.analyzeWeldLines(brep, mfg, issueId));
        issueId += 10;

        // Warpage/Deflection (Çarpılma Riski)
        issues.push(...this.analyzeWarpage(brep, mfg, issueId));
        issueId += 10;

        // Tolerances (Tolerans Analizi)
        issues.push(...this.analyzeTolerances(brep, issueId));
        issueId += 10;

        // Ejector Pin Analysis (İtici Pin Analizi)
        issues.push(...this.analyzeEjectorPins(brep, mfg, issueId));
        issueId += 10;

        // ════════════════════════════════════════════════════════
        // SÜREÇ-SPESİFİK KURALLAR
        // ════════════════════════════════════════════════════════

        // Kum Döküm Spesifik
        if (this.targetProcess === 'sand_casting') {
            issues.push(...this.analyzeSandCastingSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // Hassas (Investment) Döküm Spesifik
        if (this.targetProcess === 'investment_casting') {
            issues.push(...this.analyzeInvestmentCastingSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // Santrifüj Döküm Spesifik
        if (this.targetProcess === 'centrifugal_casting') {
            issues.push(...this.analyzeCentrifugalCastingSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // CNC Torna Spesifik
        if (this.targetProcess === 'cnc_turning') {
            issues.push(...this.analyzeCNCTurningSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // CNC 5-Eksen Spesifik
        if (this.targetProcess === 'cnc_5axis') {
            issues.push(...this.analyzeCNC5AxisSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // EDM Spesifik
        if (this.targetProcess === 'cnc_edm') {
            issues.push(...this.analyzeEDMSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // Kaynak Spesifik
        if (this.targetProcess === 'welding') {
            issues.push(...this.analyzeWeldingSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // Isıl İşlem Spesifik
        if (this.targetProcess === 'heat_treatment') {
            issues.push(...this.analyzeHeatTreatmentSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // Yüzey İşlem Spesifik
        if (this.targetProcess === 'surface_treatment') {
            issues.push(...this.analyzeSurfaceTreatmentSpecific(brep, mfg, issueId));
            issueId += 10;
        }

        // ════════════════════════════════════════════════════════
        // PRODUCT-READY: YENI GENEL ANALIZLER
        // ════════════════════════════════════════════════════════

        // Cooling Channel Analysis
        issues.push(...this.analyzeCoolingChannels(brep, mfg, issueId));
        issueId += 10;

        // Gate Location Analysis
        issues.push(...this.analyzeGateLocation(brep, mfg, issueId));
        issueId += 10;

        // Burr/Flash Risk Analysis
        issues.push(...this.analyzeBurrRisk(brep, mfg, issueId));
        issueId += 10;

        // Assembly Feasibility Analysis
        issues.push(...this.analyzeAssemblyFeasibility(brep, mfg, issueId));
        issueId += 10;

        // Feature Spacing Analysis
        issues.push(...this.analyzeFeatureSpacing(brep, mfg, issueId));
        issueId += 10;

        // Shrinkage Compensation Analysis
        issues.push(...this.analyzeShrinkageCompensation(brep, mfg, issueId));
        issueId += 10;

        // Residual Stress Analysis
        issues.push(...this.analyzeResidualStress(brep, mfg, issueId));
        issueId += 10;

        // Material-Specific Rules Analysis
        issues.push(...this.analyzeMaterialSpecificRules(brep, mfg, issueId));
        issueId += 10;

        return issues;
    }

    private analyzeWallThickness(info: WallThicknessInfo, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Analyze thin wall regions
        for (const region of info.thinWallRegions) {
            if (region.thickness < this.rules.minWallThickness) {
                issues.push({
                    id: `WT-${id++}`,
                    severity: 'critical',
                    category: 'Wall Thickness',
                    title: 'Duvar Kalınlığı Çok İnce',
                    description: `Duvar kalınlığı ${region.thickness.toFixed(2)}mm, minimum ${this.rules.minWallThickness}mm olmalı.`,
                    affectedFeatures: region.faceIds,
                    recommendation: `Duvar kalınlığını en az ${this.rules.minWallThickness}mm'ye çıkarın veya nervür ekleyin.`,
                    estimatedCostImpact: 25,
                    relatedStandard: 'ISO 294-4'
                });
            }
        }

        // Analyze thick wall regions (sink mark risk)
        for (const region of info.thickWallRegions) {
            if (region.thickness > this.rules.maxWallThickness) {
                issues.push({
                    id: `WT-${id++}`,
                    severity: 'warning',
                    category: 'Wall Thickness',
                    title: 'Duvar Kalınlığı Çok Kalın',
                    description: `Duvar kalınlığı ${region.thickness.toFixed(2)}mm, maksimum önerilen ${this.rules.maxWallThickness}mm.`,
                    affectedFeatures: region.faceIds,
                    recommendation: 'Çökme (sink mark) ve boşluk (void) riskini azaltmak için coring uygulayın.',
                    estimatedCostImpact: 15,
                    relatedStandard: 'ISO 294-4'
                });
            }
        }

        // If no issues, add a pass
        if (info.thinWallRegions.length === 0 && info.thickWallRegions.length === 0) {
            issues.push({
                id: `WT-${id++}`,
                severity: 'pass',
                category: 'Wall Thickness',
                title: 'Duvar Kalınlığı Uygun',
                description: `Ortalama duvar kalınlığı ${info.avgThickness.toFixed(2)}mm, kabul edilebilir aralıkta.`,
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    private analyzeDraftAngles(info: DraftAngleInfo, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // CNC için draft angle gerekli değil
        if (this.targetProcess === 'cnc_machining' || this.targetProcess === '3d_printing') {
            return [];
        }

        for (const surface of info.surfaceAnalysis) {
            const angleDeg = surface.draftAngle;

            if (surface.classification === 'insufficient' || angleDeg < this.rules.minDraftAngle) {
                issues.push({
                    id: `DA-${id++}`,
                    severity: 'critical',
                    category: 'Draft Angle',
                    title: 'Yetersiz Çıkış Açısı',
                    description: `Çıkış açısı ${angleDeg.toFixed(1)}°, minimum ${this.rules.minDraftAngle}° olmalı.`,
                    affectedFeatures: [surface.faceId],
                    recommendation: `Yüzeye en az ${this.rules.minDraftAngle}° (önerilen ${this.rules.recommendedDraftAngle}°) çıkış açısı verin.`,
                    estimatedCostImpact: 20,
                    relatedStandard: 'ISO 20457'
                });
            } else if (angleDeg < this.rules.recommendedDraftAngle) {
                issues.push({
                    id: `DA-${id++}`,
                    severity: 'warning',
                    category: 'Draft Angle',
                    title: 'Düşük Çıkış Açısı',
                    description: `Çıkış açısı ${angleDeg.toFixed(1)}°, önerilen ${this.rules.recommendedDraftAngle}°.`,
                    affectedFeatures: [surface.faceId],
                    recommendation: 'Kalıp ömrünü uzatmak için çıkış açısını artırın.',
                    estimatedCostImpact: 5
                });
            } else {
                issues.push({
                    id: `DA-${id++}`,
                    severity: 'pass',
                    category: 'Draft Angle',
                    title: 'Çıkış Açısı Uygun',
                    description: `Çıkış açısı ${angleDeg.toFixed(1)}°, yeterli.`,
                    affectedFeatures: [surface.faceId],
                    recommendation: ''
                });
            }
        }

        return issues;
    }

    private analyzeUndercuts(info: UndercutInfo, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        if (!info.hasUndercuts || info.undercuts.length === 0) {
            issues.push({
                id: `UC-${id++}`,
                severity: 'pass',
                category: 'Undercuts',
                title: 'Undercut Yok',
                description: 'Parçada undercut tespit edilmedi.',
                affectedFeatures: [],
                recommendation: ''
            });
            return issues;
        }

        for (const undercut of info.undercuts) {
            if (!this.rules.allowsUndercuts) {
                issues.push({
                    id: `UC-${id++}`,
                    severity: 'critical',
                    category: 'Undercuts',
                    title: 'Undercut - Üretilemez',
                    description: `${this.targetProcess} prosesi undercut desteklemiyor.`,
                    affectedFeatures: undercut.faceIds,
                    recommendation: 'Tasarımı değiştirin veya farklı üretim prosesi seçin.',
                    estimatedCostImpact: 100
                });
            } else if (undercut.depth > this.rules.maxUndercutDepth) {
                issues.push({
                    id: `UC-${id++}`,
                    severity: 'critical',
                    category: 'Undercuts',
                    title: 'Undercut Çok Derin',
                    description: `Undercut derinliği ${undercut.depth.toFixed(1)}mm, maksimum ${this.rules.maxUndercutDepth}mm.`,
                    affectedFeatures: undercut.faceIds,
                    recommendation: 'Undercut derinliğini azaltın veya tasarımı bölün.',
                    estimatedCostImpact: 40
                });
            } else {
                issues.push({
                    id: `UC-${id++}`,
                    severity: 'warning',
                    category: 'Undercuts',
                    title: 'Undercut Tespit Edildi',
                    description: `Yan çekici (side action) gerektirecek undercut mevcut. Tip: ${undercut.type}, Ciddiyet: ${undercut.severity}`,
                    affectedFeatures: undercut.faceIds,
                    recommendation: 'Yan çekici maliyeti kalıp fiyatını %20-40 artırır.',
                    estimatedCostImpact: 30
                });
            }
        }

        return issues;
    }

    private analyzeHoles(holes: HoleInfo[], startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        for (const hole of holes) {
            const depthRatio = hole.depth / hole.diameter;
            const isBlind = hole.type === 'blind';

            if (depthRatio > this.rules.maxHoleDepthRatio) {
                issues.push({
                    id: `HO-${id++}`,
                    severity: 'warning',
                    category: 'Holes',
                    title: 'Derin Delik',
                    description: `Delik derinlik/çap oranı ${depthRatio.toFixed(1)}:1, önerilen max ${this.rules.maxHoleDepthRatio}:1.`,
                    affectedFeatures: hole.faceIds,
                    recommendation: 'Derin delikler matkap kırılma riski taşır. Step drilling veya gun drilling önerilir.',
                    estimatedCostImpact: 15
                });
            } else if (isBlind && hole.depth > hole.diameter * 2) {
                issues.push({
                    id: `HO-${id++}`,
                    severity: 'info',
                    category: 'Holes',
                    title: 'Kör Delik',
                    description: `Kör delik tespit edildi. Çap: ${hole.diameter.toFixed(2)}mm, Derinlik: ${hole.depth.toFixed(2)}mm`,
                    affectedFeatures: hole.faceIds,
                    recommendation: 'Mümkünse geçme delik tercih edin (chip evacuation için).',
                    estimatedCostImpact: 5
                });
            } else {
                issues.push({
                    id: `HO-${id++}`,
                    severity: 'pass',
                    category: 'Holes',
                    title: 'Delik Uygun',
                    description: `Delik boyutları kabul edilebilir. Çap: ${hole.diameter.toFixed(2)}mm`,
                    affectedFeatures: hole.faceIds,
                    recommendation: ''
                });
            }
        }

        return issues;
    }

    private analyzeFillets(fillets: FilletInfo[], startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        for (const fillet of fillets) {
            if (fillet.radius < this.rules.minFilletRadius) {
                issues.push({
                    id: `FI-${id++}`,
                    severity: 'warning',
                    category: 'Fillets',
                    title: 'Fillet Çok Küçük',
                    description: `Fillet yarıçapı ${fillet.radius.toFixed(2)}mm, minimum önerilen ${this.rules.minFilletRadius}mm.`,
                    affectedFeatures: fillet.edgeIds,
                    recommendation: 'Küçük filletler stres konsantrasyonu oluşturur ve takım ömrünü kısaltır.',
                    estimatedCostImpact: 10
                });
            } else {
                issues.push({
                    id: `FI-${id++}`,
                    severity: 'pass',
                    category: 'Fillets',
                    title: 'Fillet Uygun',
                    description: `Fillet yarıçapı ${fillet.radius.toFixed(2)}mm, yeterli.`,
                    affectedFeatures: fillet.edgeIds,
                    recommendation: ''
                });
            }
        }

        return issues;
    }

    private analyzeRibs(ribs: RibInfo[], startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        for (const rib of ribs) {
            // Rib thickness should be 50-75% of wall thickness
            // Assuming typical wall is 2-3mm
            const optimalThickness = 1.5; // 50% of 3mm wall

            if (rib.thickness > 3.0) {
                issues.push({
                    id: `RB-${id++}`,
                    severity: 'warning',
                    category: 'Ribs',
                    title: 'Nervür Çok Kalın',
                    description: `Nervür kalınlığı ${rib.thickness.toFixed(2)}mm, duvar kalınlığının %50-75'i olmalı.`,
                    affectedFeatures: rib.faceIds,
                    recommendation: 'Kalın nervürler çökme (sink mark) oluşturur.',
                    estimatedCostImpact: 10
                });
            } else if (rib.height / rib.thickness > 5) {
                issues.push({
                    id: `RB-${id++}`,
                    severity: 'warning',
                    category: 'Ribs',
                    title: 'Nervür Çok Yüksek',
                    description: `Nervür yükseklik/kalınlık oranı ${(rib.height/rib.thickness).toFixed(1)}, önerilen max 5.`,
                    affectedFeatures: rib.faceIds,
                    recommendation: 'Yüksek nervürler dolum sorunlarına yol açabilir.',
                    estimatedCostImpact: 5
                });
            } else {
                issues.push({
                    id: `RB-${id++}`,
                    severity: 'pass',
                    category: 'Ribs',
                    title: 'Nervür Uygun',
                    description: `Nervür boyutları uygun. Kalınlık: ${rib.thickness.toFixed(2)}mm, Yükseklik: ${rib.height.toFixed(2)}mm`,
                    affectedFeatures: rib.faceIds,
                    recommendation: ''
                });
            }
        }

        return issues;
    }

    private analyzeThreads(info: ThreadInfo, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        if (!info.hasThreads || !info.threads) return issues;

        for (const thread of info.threads) {
            // Check if thread is standard
            const standardPitches: Record<number, number[]> = {
                3: [0.5],
                4: [0.7],
                5: [0.8],
                6: [1.0],
                8: [1.0, 1.25],
                10: [1.25, 1.5],
                12: [1.5, 1.75],
                16: [1.5, 2.0],
                20: [2.0, 2.5]
            };

            const nearestDia = Math.round(thread.majorDiameter);
            const validPitches = standardPitches[nearestDia] || [];
            const isStandard = validPitches.some(p => Math.abs(p - thread.pitch) < 0.05);

            if (!isStandard && thread.majorDiameter >= 3) {
                issues.push({
                    id: `TH-${id++}`,
                    severity: 'warning',
                    category: 'Threads',
                    title: 'Standart Dışı Diş',
                    description: `M${thread.majorDiameter.toFixed(1)}x${thread.pitch.toFixed(2)} standart ISO metrik diş değil.`,
                    affectedFeatures: thread.faceIds,
                    recommendation: 'Standart dişler kullanın: M3x0.5, M4x0.7, M5x0.8, M6x1.0, M8x1.25...',
                    estimatedCostImpact: 10
                });
            } else {
                issues.push({
                    id: `TH-${id++}`,
                    severity: 'pass',
                    category: 'Threads',
                    title: 'Diş Uygun',
                    description: `Standart diş: ${thread.type === 'internal' ? 'İç' : 'Dış'} M${thread.majorDiameter.toFixed(1)}x${thread.pitch.toFixed(2)}`,
                    affectedFeatures: thread.faceIds,
                    recommendation: ''
                });
            }
        }

        return issues;
    }

    private analyzePartingLine(info: PartingLineInfo, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Parting line complexity
        if (info.complexity === 'complex') {
            issues.push({
                id: `PL-${id++}`,
                severity: 'warning',
                category: 'Parting Line',
                title: 'Karmaşık Ayırma Çizgisi',
                description: `Kalıp ayırma çizgisi karmaşık, ${info.stepCount} basamak içeriyor.`,
                affectedFeatures: info.partingLine.edges,
                recommendation: 'Basit, düz bir ayırma çizgisi kalıp maliyetini düşürür.',
                estimatedCostImpact: 20
            });
        } else if (info.complexity === 'stepped' && info.stepCount > 2) {
            issues.push({
                id: `PL-${id++}`,
                severity: 'info',
                category: 'Parting Line',
                title: 'Basamaklı Ayırma Çizgisi',
                description: `Kalıp ayırma çizgisi ${info.stepCount} basamak içeriyor.`,
                affectedFeatures: info.partingLine.edges,
                recommendation: 'Basamak sayısını azaltmak kalıp imalatını kolaylaştırır.',
                estimatedCostImpact: 10
            });
        }

        // Shut-off areas
        if (info.shutoffAreas && info.shutoffAreas.length > 3) {
            const shutoffFaceIds = info.shutoffAreas.flatMap(s => s.faceIds);
            issues.push({
                id: `PL-${id++}`,
                severity: 'info',
                category: 'Parting Line',
                title: 'Çok Sayıda Shut-off Alanı',
                description: `${info.shutoffAreas.length} adet shut-off alanı tespit edildi.`,
                affectedFeatures: shutoffFaceIds,
                recommendation: 'Fazla shut-off alanları kalıp bakım maliyetini artırır.',
                estimatedCostImpact: 10
            });
        }

        // Core/Cavity balance
        const coreFaces = info.coreSide?.faceIds?.length || 0;
        const cavityFaces = info.cavitySide?.faceIds?.length || 0;
        const maxFaces = Math.max(coreFaces, cavityFaces);
        const balance = maxFaces > 0 ? Math.min(coreFaces, cavityFaces) / maxFaces : 1;

        if (balance < 0.3 && maxFaces > 0) {
            issues.push({
                id: `PL-${id++}`,
                severity: 'info',
                category: 'Parting Line',
                title: 'Dengesiz Core/Cavity',
                description: `Core: ${coreFaces} yüzey, Cavity: ${cavityFaces} yüzey. Oranı dengesiz.`,
                affectedFeatures: [],
                recommendation: 'Parting line pozisyonunu optimize edin.',
                estimatedCostImpact: 5
            });
        }

        return issues;
    }

    private analyzeSharpEdges(brep: GrupBrepResult, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Find edges without fillets or chamfers
        const fillettedEdges = new Set<string>();
        const chamferedEdges = new Set<string>();

        brep.features?.fillets?.forEach((f: FilletInfo) => f.edgeIds.forEach(e => fillettedEdges.add(e)));
        brep.features?.chamfers?.forEach((c: ChamferInfo) => c.edgeIds.forEach(e => chamferedEdges.add(e)));

        // Use geometry.sharpEdges count if available
        const sharpEdgeCount = brep.geometry?.sharpEdges || 0;

        // Also consider total edge count minus filleted/chamfered edges
        const totalEdges = brep.topology?.edgeCount || 0;
        const processedEdges = fillettedEdges.size + chamferedEdges.size;
        const unprocessedSharpEdges = Math.max(0, sharpEdgeCount - processedEdges);

        if (unprocessedSharpEdges > 5) {
            issues.push({
                id: `SE-${id++}`,
                severity: 'warning',
                category: 'Sharp Edges',
                title: 'Keskin Köşeler Tespit Edildi',
                description: `Yaklaşık ${unprocessedSharpEdges} adet fillet/chamfer uygulanmamış keskin köşe.`,
                affectedFeatures: [],
                recommendation: 'Keskin köşeler stres konsantrasyonu ve yaralanma riski oluşturur. R0.5-R1.0 fillet ekleyin.',
                estimatedCostImpact: 5
            });
        } else if (sharpEdgeCount === 0 && totalEdges > 0) {
            issues.push({
                id: `SE-${id++}`,
                severity: 'pass',
                category: 'Sharp Edges',
                title: 'Keskin Köşe Yok',
                description: 'Tüm kenarlar fillet veya chamfer ile işlenmiş.',
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    // ============================================================
    // YENİ ANALİZ METODLARI
    // ============================================================

    /**
     * Cep/Kavite Analizi - Derin cepler, dar köşeler, erişilebilirlik
     */
    private analyzePockets(pockets: any[], startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        for (const pocket of pockets) {
            const depth = pocket.depth || 0;
            const width = pocket.width || pocket.minDimension || 10;
            const aspectRatio = depth / width;

            // Derin cep kontrolü (derinlik/genişlik oranı)
            if (aspectRatio > 4) {
                issues.push({
                    id: `PK-${id++}`,
                    severity: 'critical',
                    category: 'Pockets',
                    title: 'Cep Çok Derin',
                    description: `Cep derinlik/genişlik oranı ${aspectRatio.toFixed(1)}:1. CNC takımı erişemez, kalıp çekirdeği kırılabilir.`,
                    affectedFeatures: pocket.faceIds || [],
                    recommendation: 'Cep derinliğini azaltın veya genişliği artırın. Maksimum oran 4:1 olmalı.',
                    estimatedCostImpact: 35,
                    relatedStandard: 'DIN 8580'
                });
            } else if (aspectRatio > 3) {
                issues.push({
                    id: `PK-${id++}`,
                    severity: 'warning',
                    category: 'Pockets',
                    title: 'Cep Derin',
                    description: `Cep derinlik/genişlik oranı ${aspectRatio.toFixed(1)}:1. Özel takım gerekebilir.`,
                    affectedFeatures: pocket.faceIds || [],
                    recommendation: 'Mümkünse cep derinliğini azaltın.',
                    estimatedCostImpact: 15
                });
            }

            // Dar köşe kontrolü (iç köşe yarıçapı)
            const cornerRadius = pocket.cornerRadius || 0;
            if (cornerRadius < 1 && this.targetProcess === 'cnc_machining') {
                issues.push({
                    id: `PK-${id++}`,
                    severity: 'warning',
                    category: 'Pockets',
                    title: 'Cep Köşesi Dar',
                    description: `İç köşe yarıçapı ${cornerRadius.toFixed(1)}mm. Standart freze ulaşamaz.`,
                    affectedFeatures: pocket.faceIds || [],
                    recommendation: 'İç köşe yarıçapını en az takım yarıçapı + 0.5mm yapın (genellikle R2-R3).',
                    estimatedCostImpact: 10
                });
            }

            // Taban düzlüğü kontrolü
            if (pocket.flatBottom === false) {
                issues.push({
                    id: `PK-${id++}`,
                    severity: 'info',
                    category: 'Pockets',
                    title: 'Cep Tabanı Düz Değil',
                    description: 'Cep tabanı eğimli veya kavisli.',
                    affectedFeatures: pocket.faceIds || [],
                    recommendation: 'Düz tabanlı cepler daha kolay işlenir ve kalıplanır.',
                    estimatedCostImpact: 5
                });
            }
        }

        if (pockets.length > 0 && issues.filter(i => i.severity !== 'pass').length === 0) {
            issues.push({
                id: `PK-${id++}`,
                severity: 'pass',
                category: 'Pockets',
                title: 'Cepler Uygun',
                description: `${pockets.length} adet cep üretilebilir durumda.`,
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * Boss/Çıkıntı Analizi - Patron yüksekliği, taban bağlantısı, draft
     */
    private analyzeBosses(bosses: any[], startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        for (const boss of bosses) {
            const height = boss.height || 0;
            const diameter = boss.diameter || boss.width || 5;
            const heightRatio = height / diameter;

            // Yüksek boss kontrolü
            if (heightRatio > 3) {
                issues.push({
                    id: `BS-${id++}`,
                    severity: 'warning',
                    category: 'Bosses',
                    title: 'Patron Çok Yüksek',
                    description: `Patron yükseklik/çap oranı ${heightRatio.toFixed(1)}:1. Eğilme riski var.`,
                    affectedFeatures: boss.faceIds || [],
                    recommendation: 'Patron yüksekliğini azaltın veya taban nervürü ekleyin.',
                    estimatedCostImpact: 15
                });
            }

            // Taban bağlantısı kontrolü
            if (!boss.hasFilletAtBase) {
                issues.push({
                    id: `BS-${id++}`,
                    severity: 'warning',
                    category: 'Bosses',
                    title: 'Patron Tabanında Fillet Yok',
                    description: 'Patron taban bağlantısında yuvarlatma yok.',
                    affectedFeatures: boss.faceIds || [],
                    recommendation: 'Stres konsantrasyonunu azaltmak için tabana R1-R2 fillet ekleyin.',
                    estimatedCostImpact: 10
                });
            }

            // Draft açısı kontrolü (enjeksiyon kalıplama için)
            if (this.targetProcess === 'injection_molding' || this.targetProcess === 'die_casting') {
                const draft = boss.draftAngle || 0;
                if (draft < this.rules.minDraftAngle) {
                    issues.push({
                        id: `BS-${id++}`,
                        severity: 'warning',
                        category: 'Bosses',
                        title: 'Patron Draft Açısı Yetersiz',
                        description: `Patron draft açısı ${draft.toFixed(1)}°, minimum ${this.rules.minDraftAngle}° olmalı.`,
                        affectedFeatures: boss.faceIds || [],
                        recommendation: `Patron yan yüzeylerinde en az ${this.rules.recommendedDraftAngle}° draft açısı uygulayın.`,
                        estimatedCostImpact: 20
                    });
                }
            }

            // Delik/vida yuvası kontrolü
            if (boss.hasHole) {
                const wallThickness = (diameter - (boss.holeSize || 0)) / 2;
                if (wallThickness < this.rules.minWallThickness) {
                    issues.push({
                        id: `BS-${id++}`,
                        severity: 'critical',
                        category: 'Bosses',
                        title: 'Patron Duvarı İnce',
                        description: `Patron duvar kalınlığı ${wallThickness.toFixed(1)}mm, minimum ${this.rules.minWallThickness}mm olmalı.`,
                        affectedFeatures: boss.faceIds || [],
                        recommendation: 'Patron çapını artırın veya delik boyutunu küçültün.',
                        estimatedCostImpact: 25
                    });
                }
            }
        }

        if (bosses.length > 0 && issues.filter(i => i.severity !== 'pass').length === 0) {
            issues.push({
                id: `BS-${id++}`,
                severity: 'pass',
                category: 'Bosses',
                title: 'Patronlar Uygun',
                description: `${bosses.length} adet patron üretilebilir durumda.`,
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * İnce Kesit Analizi - Ani kalınlık değişimleri, geçiş bölgeleri
     */
    private analyzeThinSections(brep: GrupBrepResult, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Geometry'den ince kesit bilgisi al (as any - gelecekte tip tanımı eklenecek)
        const geom = brep.geometry as any;
        const minThickness = geom?.minThickness || 0;
        const maxThickness = geom?.maxThickness || 0;
        const avgThickness = geom?.avgThickness || 0;

        // Ani kalınlık değişimi kontrolü
        if (maxThickness > 0 && minThickness > 0) {
            const thicknessRatio = maxThickness / minThickness;

            if (thicknessRatio > 3) {
                issues.push({
                    id: `TS-${id++}`,
                    severity: 'warning',
                    category: 'Thin Sections',
                    title: 'Ani Kalınlık Değişimi',
                    description: `Maksimum/minimum kalınlık oranı ${thicknessRatio.toFixed(1)}:1. Çarpılma ve çökme izi riski.`,
                    affectedFeatures: [],
                    recommendation: 'Kalınlık geçişlerini kademeli yapın. Oran 2:1\'i geçmemeli.',
                    estimatedCostImpact: 20,
                    relatedStandard: 'ISO 294-4'
                });
            }

            // Çok ince bölge kontrolü
            if (minThickness < this.rules.minWallThickness * 0.8) {
                issues.push({
                    id: `TS-${id++}`,
                    severity: 'critical',
                    category: 'Thin Sections',
                    title: 'Kritik İnce Kesit',
                    description: `Minimum kalınlık ${minThickness.toFixed(2)}mm, kritik seviyede.`,
                    affectedFeatures: [],
                    recommendation: `Minimum kalınlığı ${this.rules.minWallThickness}mm'ye çıkarın.`,
                    estimatedCostImpact: 30
                });
            }
        }

        // Uzun ince bölge kontrolü (akış uzunluğu)
        const boundingBox = brep.geometry?.boundingBox;
        if (boundingBox && avgThickness > 0) {
            const maxDimension = Math.max(
                (boundingBox as any).max?.x - (boundingBox as any).min?.x || 0,
                (boundingBox as any).max?.y - (boundingBox as any).min?.y || 0,
                (boundingBox as any).max?.z - (boundingBox as any).min?.z || 0
            );
            const flowRatio = maxDimension / avgThickness;

            if (flowRatio > 150 && this.targetProcess === 'injection_molding') {
                issues.push({
                    id: `TS-${id++}`,
                    severity: 'warning',
                    category: 'Thin Sections',
                    title: 'Uzun Akış Yolu',
                    description: `Akış uzunluğu/kalınlık oranı ${flowRatio.toFixed(0)}:1. Dolum problemi olabilir.`,
                    affectedFeatures: [],
                    recommendation: 'Birden fazla enjeksiyon noktası veya sıcak yolluk sistemi düşünün.',
                    estimatedCostImpact: 15
                });
            }
        }

        return issues;
    }

    /**
     * İç Köşe Analizi - Stres konsantrasyonu, keskin geçişler
     */
    private analyzeInternalCorners(brep: GrupBrepResult, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // İç köşe sayısını ve yarıçaplarını analiz et (as any - gelecekte tip tanımı eklenecek)
        const geom = brep.geometry as any;
        const internalCornerCount = geom?.internalCorners || 0;
        const minInternalRadius = geom?.minInternalRadius || 0;

        if (minInternalRadius > 0 && minInternalRadius < 0.5) {
            issues.push({
                id: `IC-${id++}`,
                severity: 'warning',
                category: 'Internal Corners',
                title: 'Keskin İç Köşe',
                description: `Minimum iç köşe yarıçapı ${minInternalRadius.toFixed(2)}mm. Stres konsantrasyonu riski.`,
                affectedFeatures: [],
                recommendation: 'İç köşelere en az R0.5-R1.0 fillet ekleyin. Yük taşıyan bölgelerde R2+ tercih edin.',
                estimatedCostImpact: 10
            });
        }

        // T-junction ve L-junction kontrolleri (as any - hasFilletAtBase gelecekte eklenecek)
        const hasSharpTJunctions = brep.features?.ribs?.some((r: any) => !r.hasFilletAtBase) || false;
        if (hasSharpTJunctions) {
            issues.push({
                id: `IC-${id++}`,
                severity: 'warning',
                category: 'Internal Corners',
                title: 'Nervür Bağlantısı Keskin',
                description: 'Nervür-duvar bağlantılarında keskin köşeler var.',
                affectedFeatures: [],
                recommendation: 'Nervür tabanlarına fillet ekleyin (nervür kalınlığının %25-50\'si kadar).',
                estimatedCostImpact: 8
            });
        }

        if (internalCornerCount === 0 || (minInternalRadius >= 0.5 && !hasSharpTJunctions)) {
            issues.push({
                id: `IC-${id++}`,
                severity: 'pass',
                category: 'Internal Corners',
                title: 'İç Köşeler Uygun',
                description: 'İç köşeler yeterli yarıçapa sahip.',
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * Çökme İzi (Sink Mark) Risk Analizi
     */
    private analyzeSinkMarks(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece enjeksiyon kalıplama ve döküm için geçerli
        if (this.targetProcess !== 'injection_molding' && this.targetProcess !== 'die_casting') {
            return issues;
        }

        // Kalın bölge kontrolü
        const thickRegions = mfg.wallThickness?.thickWallRegions || [];
        for (const region of thickRegions) {
            if (region.thickness > this.rules.maxWallThickness * 0.8) {
                issues.push({
                    id: `SM-${id++}`,
                    severity: 'warning',
                    category: 'Sink Marks',
                    title: 'Çökme İzi Riski',
                    description: `${region.thickness.toFixed(1)}mm kalınlıkta bölge. Yüzeyde çökme izi oluşabilir.`,
                    affectedFeatures: region.faceIds || [],
                    recommendation: 'Kalın bölgeleri oyarak inceleyin (coring). Nervür ile güçlendirme düşünün.',
                    estimatedCostImpact: 15
                });
            }
        }

        // Boss/nervür arkası sink mark kontrolü
        const bosses = brep.features?.bosses || [];
        const ribs = brep.features?.ribs || [];

        for (const boss of bosses as any[]) {
            if (boss.height > 5 && !boss.isCored) {
                issues.push({
                    id: `SM-${id++}`,
                    severity: 'info',
                    category: 'Sink Marks',
                    title: 'Patron Arkası Çökme Riski',
                    description: 'Yüksek patron arkasında çökme izi oluşabilir.',
                    affectedFeatures: boss.faceIds || [],
                    recommendation: 'Patron iç kısmını oyarak (coring) malzeme birikimini azaltın.',
                    estimatedCostImpact: 5
                });
            }
        }

        for (const rib of ribs) {
            const ribThickness = rib.thickness || 0;
            const wallThickness = mfg.wallThickness?.avgThickness || 2;
            if (ribThickness > wallThickness * 0.7) {
                issues.push({
                    id: `SM-${id++}`,
                    severity: 'warning',
                    category: 'Sink Marks',
                    title: 'Nervür Kalın',
                    description: `Nervür kalınlığı (${ribThickness.toFixed(1)}mm) duvar kalınlığının %70'inden fazla.`,
                    affectedFeatures: rib.faceIds || [],
                    recommendation: 'Nervür kalınlığını duvar kalınlığının %50-60\'ı olacak şekilde azaltın.',
                    estimatedCostImpact: 10
                });
            }
        }

        if (issues.length === 0) {
            issues.push({
                id: `SM-${id++}`,
                severity: 'pass',
                category: 'Sink Marks',
                title: 'Çökme İzi Riski Düşük',
                description: 'Tasarımda belirgin çökme izi riski tespit edilmedi.',
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * Klips/Snap-Fit Bağlantı Analizi
     */
    private analyzeSnapFits(snapFits: any[], startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        for (const snap of snapFits) {
            // Defleksiyon kontrolü
            const deflection = snap.deflection || 0;
            const length = snap.length || 10;
            const thickness = snap.thickness || 1;
            const strain = deflection / length;

            // Plastik için strain limiti (genelde %2-5)
            if (strain > 0.05) {
                issues.push({
                    id: `SF-${id++}`,
                    severity: 'critical',
                    category: 'Snap Fits',
                    title: 'Klips Deformasyonu Fazla',
                    description: `Klips strain değeri %${(strain * 100).toFixed(1)}. Plastik kırılabilir.`,
                    affectedFeatures: snap.faceIds || [],
                    recommendation: 'Klips uzunluğunu artırın veya kalınlığını azaltın. Strain <%5 olmalı.',
                    estimatedCostImpact: 20
                });
            } else if (strain > 0.03) {
                issues.push({
                    id: `SF-${id++}`,
                    severity: 'warning',
                    category: 'Snap Fits',
                    title: 'Klips Strain Yüksek',
                    description: `Klips strain değeri %${(strain * 100).toFixed(1)}. Yorulma riski var.`,
                    affectedFeatures: snap.faceIds || [],
                    recommendation: 'Tekrarlanan kullanım için strain değerini %2 altına düşürün.',
                    estimatedCostImpact: 10
                });
            }

            // Taban fillet kontrolü
            if (!snap.hasFilletAtBase) {
                issues.push({
                    id: `SF-${id++}`,
                    severity: 'warning',
                    category: 'Snap Fits',
                    title: 'Klips Tabanı Keskin',
                    description: 'Klips taban bağlantısında fillet yok.',
                    affectedFeatures: snap.faceIds || [],
                    recommendation: 'Klips tabanına R0.5+ fillet ekleyin.',
                    estimatedCostImpact: 5
                });
            }

            // Undercut kontrolü
            if (snap.requiresSlideAction) {
                issues.push({
                    id: `SF-${id++}`,
                    severity: 'info',
                    category: 'Snap Fits',
                    title: 'Klips Yan Çekici Gerektirir',
                    description: 'Bu klips tasarımı kalıpta yan çekici gerektirir.',
                    affectedFeatures: snap.faceIds || [],
                    recommendation: 'Mümkünse klipsi kalıp açılma yönüne paralel tasarlayın.',
                    estimatedCostImpact: 25
                });
            }
        }

        if (snapFits.length > 0 && issues.filter(i => i.severity !== 'pass').length === 0) {
            issues.push({
                id: `SF-${id++}`,
                severity: 'pass',
                category: 'Snap Fits',
                title: 'Klipsler Uygun',
                description: `${snapFits.length} adet klips bağlantısı uygun tasarlanmış.`,
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * Yüzey Kalitesi Analizi
     */
    private analyzeSurfaceFinish(brep: GrupBrepResult, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Yüzey tiplerine göre analiz
        const surfaces = brep.surfaces;
        const freeformCount = surfaces?.freeform?.length || 0;
        const totalFaces = brep.topology?.faceCount || 1;
        const freeformRatio = freeformCount / totalFaces;

        // Çok fazla serbest form yüzey kontrolü
        if (freeformRatio > 0.5 && this.targetProcess === 'cnc_machining') {
            issues.push({
                id: `SF-${id++}`,
                severity: 'info',
                category: 'Surface Finish',
                title: 'Karmaşık Yüzey Geometrisi',
                description: `Yüzeylerin %${(freeformRatio * 100).toFixed(0)}'i serbest form. 5-eksen CNC gerekebilir.`,
                affectedFeatures: [],
                recommendation: 'Mümkünse düzlemsel ve silindirik yüzeyler tercih edin.',
                estimatedCostImpact: 15
            });
        }

        // Parting line görünürlüğü (A yüzey kontrolü) - as any
        const geomSurface = brep.geometry as any;
        const hasVisibleSurfaces = geomSurface?.hasVisibleSurfaces !== false;
        if (hasVisibleSurfaces && (this.targetProcess === 'injection_molding' || this.targetProcess === 'die_casting')) {
            issues.push({
                id: `SF-${id++}`,
                severity: 'info',
                category: 'Surface Finish',
                title: 'Görünür Yüzey Kalitesi',
                description: 'Görünür yüzeylerde ayırma çizgisi ve itici izleri planlanmalı.',
                affectedFeatures: [],
                recommendation: 'Parting line\'ı görünmeyen kenarlara yerleştirin. İtici pinlerini iç yüzeylere koyun.',
                estimatedCostImpact: 5
            });
        }

        // Texture/doku uyumluluğu
        const hasConcaveSurfaces = surfaces?.freeform?.some((f: any) => f.curvature < 0) || false;
        if (hasConcaveSurfaces && this.targetProcess === 'injection_molding') {
            issues.push({
                id: `SF-${id++}`,
                severity: 'info',
                category: 'Surface Finish',
                title: 'İçbükey Yüzey',
                description: 'İçbükey yüzeylerde doku uygulaması zor olabilir.',
                affectedFeatures: [],
                recommendation: 'Derin içbükey yüzeylerde dokusuz veya EDM ile doku uygulayın.',
                estimatedCostImpact: 10
            });
        }

        return issues;
    }

    // ============================================================
    // WEB ARAŞTIRMASINDAN GELEN EK ANALİZ METODLARI
    // ============================================================

    /**
     * Hava Tuzağı (Air Trap) Analizi
     * Enjeksiyon kalıplamada hava kabarcıklarının sıkışabileceği bölgeler
     */
    private analyzeAirTraps(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece enjeksiyon kalıplama ve döküm için geçerli
        if (this.targetProcess !== 'injection_molding' && this.targetProcess !== 'die_casting') {
            return issues;
        }

        // Derin cepler ve kapalı bölgeler hava tuzağı riski taşır
        const pockets = brep.features?.pockets || [];
        const deepPockets = pockets.filter((p: any) => (p.depth || 0) > 10);

        if (deepPockets.length > 0) {
            issues.push({
                id: `AT-${id++}`,
                severity: 'warning',
                category: 'Air Traps',
                title: 'Hava Tuzağı Riski',
                description: `${deepPockets.length} adet derin cep/kavite tespit edildi. Hava sıkışması olabilir.`,
                affectedFeatures: [],
                recommendation: 'Derin ceplere hava tahliye kanalları (venting) ekleyin veya vakumlu kalıplama düşünün.',
                estimatedCostImpact: 15
            });
        }

        // Köşelerde hava tuzağı riski
        const geom = brep.geometry as any;
        const sharpCorners = geom?.sharpEdges || 0;
        if (sharpCorners > 10) {
            issues.push({
                id: `AT-${id++}`,
                severity: 'info',
                category: 'Air Traps',
                title: 'Köşelerde Hava Tuzağı',
                description: 'Keskin köşelerde hava sıkışabilir.',
                affectedFeatures: [],
                recommendation: 'Keskin köşeleri yuvarlatın ve uygun yerlere vent ekleyin.',
                estimatedCostImpact: 5
            });
        }

        // Birden fazla akış cephesi birleşim noktası
        const holes = brep.features?.holes || [];
        if (holes.length > 3) {
            issues.push({
                id: `AT-${id++}`,
                severity: 'info',
                category: 'Air Traps',
                title: 'Çoklu Akış Cephesi',
                description: `${holes.length} adet delik etrafında hava sıkışma riski.`,
                affectedFeatures: [],
                recommendation: 'Delik çevrelerinde vent kanalları düşünün.',
                estimatedCostImpact: 5
            });
        }

        if (issues.length === 0) {
            issues.push({
                id: `AT-${id++}`,
                severity: 'pass',
                category: 'Air Traps',
                title: 'Hava Tuzağı Riski Düşük',
                description: 'Belirgin hava tuzağı riski tespit edilmedi.',
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * Kaynak Çizgisi (Weld Line) Analizi
     * Malzeme akışlarının birleştiği noktalarda oluşan zayıf bölgeler
     */
    private analyzeWeldLines(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece enjeksiyon kalıplama için geçerli
        if (this.targetProcess !== 'injection_molding') {
            return issues;
        }

        // Delikler etrafında weld line oluşur
        const holes = brep.features?.holes || [];
        if (holes.length > 0) {
            issues.push({
                id: `WL-${id++}`,
                severity: 'info',
                category: 'Weld Lines',
                title: 'Kaynak Çizgisi Bekleniyor',
                description: `${holes.length} adet delik etrafında kaynak çizgisi oluşacak.`,
                affectedFeatures: [],
                recommendation: 'Kaynak çizgilerinin görünür yüzeylerde olmadığından emin olun. Kapı konumunu optimize edin.',
                estimatedCostImpact: 5
            });
        }

        // Kalın-ince geçişlerde weld line riski (as any - uniformity gelecekte eklenecek)
        const wallThicknessAny = mfg.wallThickness as any;
        const hasThicknessVariation = wallThicknessAny?.uniformity && wallThicknessAny.uniformity < 0.7;
        if (hasThicknessVariation) {
            issues.push({
                id: `WL-${id++}`,
                severity: 'warning',
                category: 'Weld Lines',
                title: 'Düzensiz Kalınlık - Kaynak Çizgisi',
                description: 'Kalınlık değişimlerinde akış hızı farklılığı kaynak çizgisi oluşturur.',
                affectedFeatures: [],
                recommendation: 'Duvar kalınlığını homojen tutun veya kademeli geçişler kullanın.',
                estimatedCostImpact: 10
            });
        }

        // Birden fazla kapı (gate) kullanılırsa
        const boundingBox = brep.geometry?.boundingBox;
        if (boundingBox) {
            const maxDim = Math.max(
                (boundingBox as any).max?.x - (boundingBox as any).min?.x || 0,
                (boundingBox as any).max?.y - (boundingBox as any).min?.y || 0,
                (boundingBox as any).max?.z - (boundingBox as any).min?.z || 0
            );
            if (maxDim > 200) {
                issues.push({
                    id: `WL-${id++}`,
                    severity: 'warning',
                    category: 'Weld Lines',
                    title: 'Büyük Parça - Çoklu Kapı',
                    description: `Parça boyutu (${maxDim.toFixed(0)}mm) büyük, birden fazla enjeksiyon kapısı gerekebilir.`,
                    affectedFeatures: [],
                    recommendation: 'Çoklu kapı kullanılırsa kaynak çizgisi konumlarını simülasyon ile belirleyin.',
                    estimatedCostImpact: 15
                });
            }
        }

        return issues;
    }

    /**
     * Çarpılma (Warpage/Deflection) Analizi
     * Soğuma sonrası deformasyon riski
     */
    private analyzeWarpage(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Enjeksiyon kalıplama, döküm ve 3D baskı için geçerli
        if (this.targetProcess !== 'injection_molding' &&
            this.targetProcess !== 'die_casting' &&
            this.targetProcess !== '3d_printing') {
            return issues;
        }

        // Düzensiz duvar kalınlığı çarpılmaya neden olur
        const wallInfo = mfg.wallThickness;
        if (wallInfo) {
            const thicknessRatio = (wallInfo.maxThickness || 1) / (wallInfo.minThickness || 1);
            if (thicknessRatio > 2) {
                issues.push({
                    id: `WP-${id++}`,
                    severity: 'warning',
                    category: 'Warpage',
                    title: 'Çarpılma Riski - Kalınlık Farkı',
                    description: `Kalınlık oranı ${thicknessRatio.toFixed(1)}:1. Farklı soğuma hızları çarpılmaya neden olur.`,
                    affectedFeatures: [],
                    recommendation: 'Duvar kalınlığını homojenleştirin. Kalın bölgeleri oyarak (coring) inceleyin.',
                    estimatedCostImpact: 20,
                    relatedStandard: 'ISO 294-4'
                });
            }
        }

        // Uzun ince parçalar çarpılmaya eğilimli
        const geom = brep.geometry as any;
        const boundingBox = brep.geometry?.boundingBox;
        if (boundingBox) {
            const dims = [
                (boundingBox as any).max?.x - (boundingBox as any).min?.x || 0,
                (boundingBox as any).max?.y - (boundingBox as any).min?.y || 0,
                (boundingBox as any).max?.z - (boundingBox as any).min?.z || 0
            ].sort((a, b) => b - a);

            const aspectRatio = dims[0] / (dims[2] || 1);
            if (aspectRatio > 10) {
                issues.push({
                    id: `WP-${id++}`,
                    severity: 'warning',
                    category: 'Warpage',
                    title: 'Uzun İnce Parça - Çarpılma Riski',
                    description: `Boy/kalınlık oranı ${aspectRatio.toFixed(0)}:1. Çarpılma olasılığı yüksek.`,
                    affectedFeatures: [],
                    recommendation: 'Nervür ekleyerek rijitliği artırın. Kalıpta soğutma kanallarını optimize edin.',
                    estimatedCostImpact: 15
                });
            }
        }

        // Asimetrik tasarım çarpılma riski
        const symmetry = mfg.symmetry;
        if (symmetry && symmetry.overallSymmetryScore < 0.5) {
            issues.push({
                id: `WP-${id++}`,
                severity: 'info',
                category: 'Warpage',
                title: 'Asimetrik Tasarım',
                description: 'Asimetrik parçalar eşit soğumaz ve çarpılabilir.',
                affectedFeatures: [],
                recommendation: 'Mümkünse tasarımı simetrik yapın veya soğutma kanallarını asimetrik yerleştirin.',
                estimatedCostImpact: 10
            });
        }

        if (issues.length === 0) {
            issues.push({
                id: `WP-${id++}`,
                severity: 'pass',
                category: 'Warpage',
                title: 'Çarpılma Riski Düşük',
                description: 'Tasarımda belirgin çarpılma riski tespit edilmedi.',
                affectedFeatures: [],
                recommendation: ''
            });
        }

        return issues;
    }

    /**
     * Tolerans Analizi
     * Boyutsal tolerans ve işlenebilirlik kontrolü
     */
    private analyzeTolerances(brep: GrupBrepResult, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Delik toleransları
        const holes = brep.features?.holes || [];
        const tightToleranceHoles = holes.filter((h: any) => h.tolerance && h.tolerance < 0.05);

        if (tightToleranceHoles.length > 0) {
            issues.push({
                id: `TOL-${id++}`,
                severity: 'warning',
                category: 'Tolerances',
                title: 'Sıkı Delik Toleransı',
                description: `${tightToleranceHoles.length} adet delik ±0.05mm altında tolerans gerektiriyor.`,
                affectedFeatures: [],
                recommendation: 'Sıkı toleranslı delikler için raybalama veya taşlama gibi ikincil işlem gerekebilir.',
                estimatedCostImpact: 15
            });
        }

        // Genel boyut toleransları
        const boundingBox = brep.geometry?.boundingBox;
        if (boundingBox) {
            const maxDim = Math.max(
                (boundingBox as any).max?.x - (boundingBox as any).min?.x || 0,
                (boundingBox as any).max?.y - (boundingBox as any).min?.y || 0,
                (boundingBox as any).max?.z - (boundingBox as any).min?.z || 0
            );

            // Büyük parçalarda sıkı tolerans zor
            if (maxDim > 300) {
                issues.push({
                    id: `TOL-${id++}`,
                    severity: 'info',
                    category: 'Tolerances',
                    title: 'Büyük Parça Toleransı',
                    description: `Parça boyutu ${maxDim.toFixed(0)}mm. Büyük parçalarda sıkı tolerans tutmak zordur.`,
                    affectedFeatures: [],
                    recommendation: 'Kritik boyutlarda GD&T (Geometrik Toleranslama) kullanın. Montaj için pim/yuva referans alın.',
                    estimatedCostImpact: 10
                });
            }
        }

        // Proses bazlı tolerans önerileri
        const processTolerances: Record<ManufacturingProcess, string> = {
            'injection_molding': '±0.1-0.3mm (standart), ±0.05mm (hassas kalıp)',
            'die_casting': '±0.2-0.5mm (standart), ±0.1mm (hassas döküm)',
            'cnc_machining': '±0.025-0.05mm (standart), ±0.005mm (hassas işleme)',
            'sheet_metal': '±0.2-0.5mm (bükme), ±0.1mm (kesme)',
            '3d_printing': '±0.1-0.3mm (FDM), ±0.05mm (SLA/SLS)',
            'forging': '±0.5-2mm (standart), ±0.3mm (hassas dövme)',
            'sand_casting': '±1.0-2.0mm (standart), ±0.5mm (hassas kum döküm)',
            'investment_casting': '±0.1-0.3mm (standart), ±0.05mm (hassas)',
            'shell_mold_casting': '±0.3-0.8mm (standart)',
            'centrifugal_casting': '±0.5-1.5mm (standart)',
            'cnc_turning': '±0.025-0.05mm (standart), ±0.005mm (hassas)',
            'cnc_5axis': '±0.01-0.03mm (standart), ±0.005mm (hassas)',
            'cnc_edm': '±0.005-0.02mm (tel erozyon), ±0.01mm (dalma)',
            'cnc_grinding': '±0.002-0.01mm (standart), ±0.001mm (hassas)',
            'welding': '±0.5-2.0mm (kaynak dikişi), ±0.3mm (robot kaynak)',
            'heat_treatment': '±0.05-0.2mm (çarpılma payı)',
            'surface_treatment': '±0.005-0.05mm (kaplama kalınlığı)'
        };

        issues.push({
            id: `TOL-${id++}`,
            severity: 'info',
            category: 'Tolerances',
            title: 'Proses Tolerans Bilgisi',
            description: `${this.targetProcess} için tipik tolerans: ${processTolerances[this.targetProcess]}`,
            affectedFeatures: [],
            recommendation: 'Kritik olmayan boyutlarda toleransı gevşetin, maliyeti düşürün.'
        });

        return issues;
    }

    /**
     * İtici Pin (Ejector Pin) Analizi
     * Kalıptan çıkarma için itici pin konumları ve izleri
     */
    private analyzeEjectorPins(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece enjeksiyon kalıplama ve döküm için geçerli
        if (this.targetProcess !== 'injection_molding' && this.targetProcess !== 'die_casting') {
            return issues;
        }

        // Yüzey alanına göre itici ihtiyacı
        const surfaceArea = brep.geometry?.surfaceArea || 0;
        const estimatedEjectors = Math.ceil(surfaceArea / 500); // Her 500mm² için 1 itici tahmini

        if (estimatedEjectors > 10) {
            issues.push({
                id: `EP-${id++}`,
                severity: 'info',
                category: 'Ejector Pins',
                title: 'Çok Sayıda İtici Gerekli',
                description: `Tahmini ${estimatedEjectors} adet itici pin gerekebilir.`,
                affectedFeatures: [],
                recommendation: 'İtici pinleri görünmeyen yüzeylere yerleştirin. Stripper plate alternatifini değerlendirin.',
                estimatedCostImpact: 10
            });
        }

        // Derin ceplerde itici zorluğu
        const pockets = brep.features?.pockets || [];
        const deepPockets = pockets.filter((p: any) => (p.depth || 0) > 15);
        if (deepPockets.length > 0) {
            issues.push({
                id: `EP-${id++}`,
                severity: 'warning',
                category: 'Ejector Pins',
                title: 'Derin Cep - İtici Zorluğu',
                description: `${deepPockets.length} adet derin cepte itici yerleştirmek zor olabilir.`,
                affectedFeatures: [],
                recommendation: 'Derin ceplerde sleeve ejector veya air ejector kullanın.',
                estimatedCostImpact: 15
            });
        }

        // Draft açısı ve itici ilişkisi (as any - minAngle gelecekte eklenecek)
        const draftInfo = mfg.draftAngles as any;
        if (draftInfo && draftInfo.minAngle < 1) {
            issues.push({
                id: `EP-${id++}`,
                severity: 'warning',
                category: 'Ejector Pins',
                title: 'Düşük Draft - İtici Hasarı Riski',
                description: 'Yetersiz draft açısı parçanın kalıptan çıkarılırken hasar görmesine neden olabilir.',
                affectedFeatures: [],
                recommendation: 'Draft açısını artırın veya daha fazla itici pin kullanın.',
                estimatedCostImpact: 10
            });
        }

        // Görünür yüzeylerde itici izi uyarısı
        issues.push({
            id: `EP-${id++}`,
            severity: 'info',
            category: 'Ejector Pins',
            title: 'İtici İzi Planlaması',
            description: 'İtici pinleri parçada küçük yuvarlak izler bırakır.',
            affectedFeatures: [],
            recommendation: 'İtici pinlerini A yüzeylerden (görünür) uzak tutun. İç yüzey veya montaj alanlarına yerleştirin.'
        });

        return issues;
    }

    // ============================================================
    // SCORE CALCULATION
    // ============================================================

    private calculateScores(
        brep: GrupBrepResult,
        mfg: ManufacturingAnalysis,
        issues: DFMIssue[]
    ): DFMScore {
        const categoryScores: Record<string, number> = {};

        // Group issues by category
        const issuesByCategory = new Map<string, DFMIssue[]>();
        for (const issue of issues) {
            if (!issuesByCategory.has(issue.category)) {
                issuesByCategory.set(issue.category, []);
            }
            issuesByCategory.get(issue.category)!.push(issue);
        }

        // Calculate score for each category
        for (const [category, categoryIssues] of issuesByCategory) {
            let score = 100;
            for (const issue of categoryIssues) {
                switch (issue.severity) {
                    case 'critical': score -= 30; break;
                    case 'warning': score -= 15; break;
                    case 'info': score -= 5; break;
                    case 'pass': break; // No penalty
                }
            }
            categoryScores[category] = Math.max(0, score);
        }

        // Map to DFMScore
        const scores: DFMScore = {
            overall: 0,
            processGeometryFit: categoryScores['Process Geometry Fit'] ?? 100,
            wallThickness: categoryScores['Wall Thickness'] ?? 100,
            draftAngles: categoryScores['Draft Angle'] ?? 100,
            undercuts: categoryScores['Undercuts'] ?? 100,
            holes: categoryScores['Holes'] ?? 100,
            fillets: categoryScores['Fillets'] ?? 100,
            ribs: categoryScores['Ribs'] ?? 100,
            threads: categoryScores['Threads'] ?? 100,
            partingLine: categoryScores['Parting Line'] ?? 100,
            symmetry: mfg.symmetry ? (mfg.symmetry.overallSymmetryScore * 100) : 100,
            // Yeni kategoriler
            pockets: categoryScores['Pockets'] ?? 100,
            bosses: categoryScores['Bosses'] ?? 100,
            thinSections: categoryScores['Thin Sections'] ?? 100,
            internalCorners: categoryScores['Internal Corners'] ?? 100,
            sinkMarks: categoryScores['Sink Marks'] ?? 100,
            snapFits: categoryScores['Snap Fits'] ?? 100,
            surfaceFinish: categoryScores['Surface Finish'] ?? 100,
            // Web araştırmasından eklenen kategoriler
            airTraps: categoryScores['Air Traps'] ?? 100,
            weldLines: categoryScores['Weld Lines'] ?? 100,
            warpage: categoryScores['Warpage'] ?? 100,
            tolerances: categoryScores['Tolerances'] ?? 100,
            ejectorPins: categoryScores['Ejector Pins'] ?? 100
        };

        // Calculate overall (weighted average)
        // processGeometryFit en yuksek agirliga sahip - yanlis proses secilirse
        // tum skoru dusurur (ornegin torna icin duz parca = dusuk skor)
        const weights = {
            processGeometryFit: 30, // EN YUKSEK AGIRLIK - proses-geometri uyumu
            wallThickness: 15,
            draftAngles: 10,
            undercuts: 10,
            holes: 6,
            fillets: 5,
            ribs: 4,
            threads: 3,
            partingLine: 5,
            symmetry: 4,
            pockets: 4,
            bosses: 3,
            thinSections: 3,
            internalCorners: 2,
            sinkMarks: 2,
            snapFits: 0,
            surfaceFinish: 0,
            airTraps: 3,
            weldLines: 2,
            warpage: 3,
            tolerances: 2,
            ejectorPins: 1
        };

        let weightedSum = 0;
        let totalWeight = 0;
        for (const [key, weight] of Object.entries(weights)) {
            weightedSum += (scores[key as keyof typeof scores] || 0) * weight;
            totalWeight += weight;
        }

        scores.overall = Math.round(weightedSum / totalWeight);

        // Ek ceza: processGeometryFit cok dusukse overall'i sinirla
        // (agirlik sistemi yetmezse bile, yanlis proseste 70+ puan olmamali)
        if (scores.processGeometryFit < 30) {
            scores.overall = Math.min(scores.overall, 25);
        } else if (scores.processGeometryFit < 50) {
            scores.overall = Math.min(scores.overall, 45);
        } else if (scores.processGeometryFit < 70) {
            scores.overall = Math.min(scores.overall, 65);
        }

        return scores;
    }

    // ============================================================
    // COST ESTIMATION
    // ============================================================

    private estimateCost(issues: DFMIssue[], brep: GrupBrepResult, mfg?: ManufacturingAnalysis, costParams?: DFMCostParams): CostEstimate {
        // ── Legacy normalize hesaplama (geriye uyumluluk) ──
        const baseCost = 100;
        let totalImpact = 0;
        for (const issue of issues) {
            if (issue.estimatedCostImpact) {
                totalImpact += issue.estimatedCostImpact * (
                    issue.severity === 'critical' ? 1.0 :
                    issue.severity === 'warning' ? 0.5 :
                    0.2
                );
            }
        }
        totalImpact = Math.min(100, totalImpact);
        const currentCost = baseCost * (1 + totalImpact / 100);
        const breakdown = {
            tooling: currentCost * 0.35,
            material: currentCost * 0.25,
            machining: currentCost * 0.20,
            assembly: currentCost * 0.10,
            qualityControl: currentCost * 0.10
        };

        // ── Gercek fizik-bazli maliyet hesaplama ──
        let detailedResult: CostEstimationResult | undefined;
        let processComparisons: ProcessCostComparison[] | undefined;

        try {
            const costEngine = getCostEngine();
            const geom = brep.geometry;

            if (geom && geom.volume > 0) {
                // BREP + manufacturing analysis verilerinden PartGeometry olustur
                const threadCount = mfg?.threads?.threads?.length || 0;
                const undercutCount = mfg?.undercuts?.undercuts?.length || 0;
                const maxWall = mfg?.wallThickness?.maxThickness;
                const minWall = mfg?.wallThickness?.minThickness;

                const partGeometry: PartGeometry = {
                    volumeCm3: geom.volume,
                    surfaceAreaCm2: geom.surfaceArea,
                    boundingBox: this.extractBoundingBox(brep),
                    complexityScore: this.calculateComplexityScore(brep),
                    holeCount: brep.features?.holes?.length || 0,
                    pocketCount: brep.features?.pockets?.length || 0,
                    filletCount: brep.features?.fillets?.length || 0,
                    chamferCount: brep.features?.chamfers?.length || 0,
                    threadCount,
                    ribCount: brep.features?.ribs?.length || 0,
                    undercutCount,
                    maxWallThickness: maxWall ?? undefined as any,
                    minWallThickness: minWall ?? undefined as any,
                };

                // Malzeme secimi: costParams'tan veya surece uygun varsayilan
                const materialId = costParams?.materialId || this.selectDefaultMaterial(this.targetProcess);

                const costInput: CostEstimationInput = {
                    partGeometry,
                    materialId,
                    batchSize: costParams?.batchSize ?? 100,
                    annualVolume: costParams?.annualVolume ?? 1000,
                    profitMarginPercent: costParams?.profitMarginPercent ?? 15,
                    overheadPercent: costParams?.overheadPercent ?? 10,
                    qualityControlPercent: costParams?.qualityControlPercent ?? 3,
                    tightestToleranceMM: costParams?.tightestToleranceMM ?? 0.1,
                    requiredSurfaceRa: costParams?.requiredSurfaceRa ?? 3.2,
                    outputCurrency: costParams?.outputCurrency ?? 'USD',
                };

                detailedResult = costEngine.estimate(costInput);
                processComparisons = detailedResult.processComparisons;
            }
        } catch (e) {
            // Gercek maliyet hesaplama basarisiz olursa legacy'ye geri don
            console.warn('[FikoDFMAnalyzer] Detayli maliyet hesaplama hatasi, legacy kullaniliyor:', e);
        }

        // Custom items toplami
        const customItemsTotal = (costParams?.customCostItems || [])
            .filter(c => c.enabled)
            .reduce((sum, c) => sum + c.costPerPart, 0);

        const bestPerPart = detailedResult?.bestCostPerPart ?? currentCost;
        const grandTotal = bestPerPart + customItemsTotal;

        return {
            baselineCost: baseCost,
            currentCost: Math.round(currentCost),
            potentialSavings: Math.round(currentCost - baseCost),
            savingsPercentage: Math.round(totalImpact),
            costBreakdown: breakdown,

            // Detayli sonuclar
            detailedResult,
            processComparisons,
            partWeightKg: detailedResult?.partWeightKg,
            bestCostPerPart: detailedResult?.bestCostPerPart,
            bestCostPerKg: detailedResult?.bestCostPerKg,
            recommendedProcess: detailedResult?.recommendedProcessName,
            recommendedProcessTR: detailedResult?.recommendedProcessNameTR,
            feasibility: detailedResult?.feasibilitySignal,
            currency: detailedResult?.currency,
            customItemsTotal,
            grandTotal,
        };
    }

    /** BREP sonucundan bounding box cikar (mm) */
    private extractBoundingBox(brep: GrupBrepResult): { x: number; y: number; z: number } {
        if (brep.geometry?.boundingBox) {
            const bb = brep.geometry.boundingBox;
            return {
                x: (bb.max.x - bb.min.x),
                y: (bb.max.y - bb.min.y),
                z: (bb.max.z - bb.min.z)
            };
        }
        if (brep.geometry?.volume > 0) {
            const side = Math.cbrt(brep.geometry.volume) * 10;
            return { x: side * 1.5, y: side * 1.2, z: side };
        }
        return { x: 100, y: 100, z: 50 };
    }

    /** Geometrik karmasiklik skoru hesapla (0-100) */
    private calculateComplexityScore(brep: GrupBrepResult): number {
        let score = 20; // Baz skor
        score += Math.min(15, (brep.features?.holes?.length || 0) * 2);
        score += Math.min(15, (brep.features?.pockets?.length || 0) * 3);
        score += Math.min(10, (brep.features?.fillets?.length || 0) * 0.5);
        score += Math.min(10, (brep.features?.chamfers?.length || 0) * 0.5);
        score += Math.min(10, (brep.features?.ribs?.length || 0) * 2);
        // topology.edgeCount gives overall complexity hint
        score += Math.min(10, Math.floor((brep.topology?.edgeCount || 0) / 50));
        return Math.min(100, score);
    }

    /** Surece uygun varsayilan malzeme sec */
    private selectDefaultMaterial(process: ManufacturingProcess): string {
        const defaults: Record<string, string> = {
            injection_molding: 'ABS',
            die_casting: 'ADC12',
            cnc_machining: 'AL6061',
            sheet_metal: 'ST37',
            '3d_printing': 'PLA',
            forging: 'CK45',
            sand_casting: 'GJL-250',
            investment_casting: 'AISI304',
            shell_mold_casting: 'GJL-250',
            centrifugal_casting: 'CuSn12',
            cnc_turning: 'CK45',
            cnc_5axis: 'AL7075',
            cnc_edm: '4140',
            cnc_grinding: '4140',
            welding: 'ST37',
            heat_treatment: 'CK45',
            surface_treatment: 'AL6061',
        };
        return defaults[process] || 'ST37';
    }

    // ============================================================
    // REPORT GENERATION
    // ============================================================

    private generateSections(issues: DFMIssue[]): DFMReportSection[] {
        const sections: DFMReportSection[] = [];

        // Group by category
        const byCategory = new Map<string, DFMIssue[]>();
        for (const issue of issues) {
            if (!byCategory.has(issue.category)) {
                byCategory.set(issue.category, []);
            }
            byCategory.get(issue.category)!.push(issue);
        }

        for (const [category, categoryIssues] of byCategory) {
            // Calculate section score
            let score = 100;
            const recommendations: string[] = [];

            for (const issue of categoryIssues) {
                switch (issue.severity) {
                    case 'critical': score -= 30; break;
                    case 'warning': score -= 15; break;
                    case 'info': score -= 5; break;
                }
                if (issue.recommendation) {
                    recommendations.push(issue.recommendation);
                }
            }

            sections.push({
                title: category,
                score: Math.max(0, score),
                issues: categoryIssues,
                recommendations: [...new Set(recommendations)] // Unique
            });
        }

        // Sort by score (worst first)
        sections.sort((a, b) => a.score - b.score);

        return sections;
    }

    private generateTopRecommendations(issues: DFMIssue[]): string[] {
        // Get critical and warning issues
        const important = issues.filter(i =>
            i.severity === 'critical' || i.severity === 'warning'
        );

        // Sort by cost impact
        important.sort((a, b) =>
            (b.estimatedCostImpact || 0) - (a.estimatedCostImpact || 0)
        );

        // Get unique recommendations
        const recommendations: string[] = [];
        for (const issue of important) {
            if (issue.recommendation && !recommendations.includes(issue.recommendation)) {
                recommendations.push(issue.recommendation);
            }
            if (recommendations.length >= 5) break;
        }

        return recommendations;
    }

    private getManufacturabilityRating(score: number): DFMReport['manufacturabilityRating'] {
        if (score >= 90) return 'excellent';
        if (score >= 75) return 'good';
        if (score >= 60) return 'acceptable';
        if (score >= 40) return 'poor';
        return 'unmanufacturable';
    }

    // ============================================================
    // REPORT FORMATTING
    // ============================================================

    /**
     * Generate formatted text report
     */
    formatTextReport(report: DFMReport): string {
        const lines: string[] = [];
        const width = 70;

        const hr = '═'.repeat(width);
        const hrThin = '─'.repeat(width);

        // Header
        lines.push('╔' + hr + '╗');
        lines.push('║' + this.centerText('DFM ANALİZ RAPORU', width) + '║');
        lines.push('║' + this.centerText(report.partName, width) + '║');
        lines.push('╠' + hr + '╣');

        // Overall Score
        const ratingEmoji = {
            excellent: '🟢',
            good: '🟡',
            acceptable: '🟠',
            poor: '🔴',
            unmanufacturable: '⛔'
        };

        lines.push('║ GENEL SKOR: ' + report.overallScore + '/100 ' +
            ratingEmoji[report.manufacturabilityRating] +
            ' (' + this.translateRating(report.manufacturabilityRating) + ')' +
            ' '.repeat(width - 40) + '║');
        lines.push('║ Üretim Prosesi: ' + this.translateProcess(report.targetProcess) +
            ' '.repeat(width - 25 - this.translateProcess(report.targetProcess).length) + '║');
        lines.push('╠' + hr + '╣');

        // Critical Issues
        if (report.criticalIssues.length > 0) {
            lines.push('║ ❌ KRİTİK SORUNLAR (' + report.criticalIssues.length + ')' +
                ' '.repeat(width - 25) + '║');
            for (const issue of report.criticalIssues.slice(0, 5)) {
                lines.push('║   • ' + this.truncate(issue.description, width - 8) + '║');
            }
            lines.push('╠' + hr + '╣');
        }

        // Warnings
        if (report.warnings.length > 0) {
            lines.push('║ ⚠️ UYARILAR (' + report.warnings.length + ')' +
                ' '.repeat(width - 20) + '║');
            for (const issue of report.warnings.slice(0, 5)) {
                lines.push('║   • ' + this.truncate(issue.description, width - 8) + '║');
            }
            lines.push('╠' + hr + '╣');
        }

        // Passed
        lines.push('║ ✅ UYGUN (' + report.passed.length + ')' +
            ' '.repeat(width - 16) + '║');
        lines.push('╠' + hr + '╣');

        // Cost Estimate
        lines.push('║ TAHMİNİ MALİYET ETKİSİ' + ' '.repeat(width - 24) + '║');
        lines.push('║   • Optimum tasarım: $' + report.costEstimate.baselineCost + '/parça' +
            ' '.repeat(width - 35) + '║');
        lines.push('║   • Mevcut tasarım:  $' + report.costEstimate.currentCost + '/parça' +
            ' '.repeat(width - 35) + '║');
        if (report.costEstimate.savingsPercentage > 0) {
            lines.push('║   • Potansiyel tasarruf: %' + report.costEstimate.savingsPercentage +
                ' '.repeat(width - 35) + '║');
        }
        lines.push('╠' + hr + '╣');

        // Top Recommendations
        if (report.topRecommendations.length > 0) {
            lines.push('║ ÖNCELİKLİ ÖNERİLER' + ' '.repeat(width - 20) + '║');
            for (let i = 0; i < Math.min(3, report.topRecommendations.length); i++) {
                lines.push('║ ' + (i + 1) + '. ' + this.truncate(report.topRecommendations[i], width - 6) + '║');
            }
        }

        // Footer
        lines.push('╠' + hr + '╣');
        lines.push('║ Rapor ID: ' + report.reportId +
            ' '.repeat(width - 12 - report.reportId.length) + '║');
        lines.push('║ Tarih: ' + report.generatedAt.toISOString().split('T')[0] +
            ' '.repeat(width - 20) + '║');
        lines.push('║ 🤖 FikoYT DFM Analyzer v' + report.analysisVersion +
            ' '.repeat(width - 30) + '║');
        lines.push('╚' + hr + '╝');

        return lines.join('\n');
    }

    /**
     * Generate JSON report for API
     */
    formatJSONReport(report: DFMReport): string {
        // Remove large data for API response
        const apiReport = {
            ...report,
            brepAnalysis: undefined,
            manufacturingAnalysis: undefined
        };
        return JSON.stringify(apiReport, null, 2);
    }

    /**
     * Generate HTML report
     */
    formatHTMLReport(report: DFMReport): string {
        const severityColors = {
            critical: '#dc3545',
            warning: '#ffc107',
            info: '#17a2b8',
            pass: '#28a745'
        };

        return `
<!DOCTYPE html>
<html lang="tr">
<head>
    <meta charset="UTF-8">
    <title>DFM Raporu - ${report.partName}</title>
    <style>
        body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #1e3c72, #2a5298); color: white; padding: 30px; border-radius: 10px; }
        .score { font-size: 48px; font-weight: bold; }
        .score-bar { background: #e0e0e0; border-radius: 10px; height: 20px; margin: 10px 0; }
        .score-fill { height: 100%; border-radius: 10px; transition: width 0.5s; }
        .section { margin: 20px 0; padding: 20px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .critical { border-left: 5px solid ${severityColors.critical}; }
        .warning { border-left: 5px solid ${severityColors.warning}; }
        .info { border-left: 5px solid ${severityColors.info}; }
        .pass { border-left: 5px solid ${severityColors.pass}; }
        .issue { padding: 10px; margin: 5px 0; background: #f8f9fa; border-radius: 5px; }
        .cost-box { background: #f0f7ff; padding: 20px; border-radius: 10px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
    </style>
</head>
<body>
    <div class="header">
        <h1>DFM Analiz Raporu</h1>
        <h2>${report.partName}</h2>
        <div class="score">${report.overallScore}/100</div>
        <div>${this.translateRating(report.manufacturabilityRating)}</div>
    </div>

    <div class="section">
        <h3>Skor Dağılımı</h3>
        <table>
            ${Object.entries(report.scores).filter(([k]) => k !== 'overall').map(([key, value]) => `
            <tr>
                <td>${this.translateScoreKey(key)}</td>
                <td>
                    <div class="score-bar">
                        <div class="score-fill" style="width: ${value}%; background: ${this.getScoreColor(value)}"></div>
                    </div>
                </td>
                <td>${value}</td>
            </tr>
            `).join('')}
        </table>
    </div>

    ${report.criticalIssues.length > 0 ? `
    <div class="section critical">
        <h3>❌ Kritik Sorunlar (${report.criticalIssues.length})</h3>
        ${report.criticalIssues.map(i => `
        <div class="issue">
            <strong>${i.title}</strong><br>
            ${i.description}<br>
            <em>💡 ${i.recommendation}</em>
        </div>
        `).join('')}
    </div>
    ` : ''}

    ${report.warnings.length > 0 ? `
    <div class="section warning">
        <h3>⚠️ Uyarılar (${report.warnings.length})</h3>
        ${report.warnings.map(i => `
        <div class="issue">
            <strong>${i.title}</strong><br>
            ${i.description}<br>
            <em>💡 ${i.recommendation}</em>
        </div>
        `).join('')}
    </div>
    ` : ''}

    <div class="cost-box">
        <h3>💰 Maliyet Analizi</h3>
        <p>Optimum tasarım: <strong>$${report.costEstimate.baselineCost}/parça</strong></p>
        <p>Mevcut tasarım: <strong>$${report.costEstimate.currentCost}/parça</strong></p>
        ${report.costEstimate.savingsPercentage > 0 ? `
        <p>Potansiyel tasarruf: <strong>%${report.costEstimate.savingsPercentage}</strong></p>
        ` : ''}
    </div>

    <div class="section pass">
        <h3>✅ Uygun Özellikler (${report.passed.length})</h3>
        <p>${report.passed.length} özellik üretim standartlarına uygun.</p>
    </div>

    <footer style="text-align: center; color: #666; margin-top: 40px;">
        <p>Rapor ID: ${report.reportId} | Tarih: ${report.generatedAt.toLocaleDateString('tr-TR')}</p>
        <p>🤖 FikoYT DFM Analyzer v${report.analysisVersion}</p>
    </footer>
</body>
</html>`;
    }

    // Helper methods
    private centerText(text: string, width: number): string {
        const padding = Math.max(0, width - text.length);
        const left = Math.floor(padding / 2);
        const right = padding - left;
        return ' '.repeat(left) + text + ' '.repeat(right);
    }

    private truncate(text: string, maxLen: number): string {
        if (text.length <= maxLen) {
            return text + ' '.repeat(maxLen - text.length);
        }
        return text.substring(0, maxLen - 3) + '...';
    }

    private translateRating(rating: string): string {
        const translations: Record<string, string> = {
            excellent: 'Mükemmel Üretilebilirlik',
            good: 'İyi Üretilebilirlik',
            acceptable: 'Kabul Edilebilir',
            poor: 'Zayıf Üretilebilirlik',
            unmanufacturable: 'Üretilemez'
        };
        return translations[rating] || rating;
    }

    private translateProcess(process: ManufacturingProcess): string {
        const translations: Record<string, string> = {
            injection_molding: 'Enjeksiyon Kalıplama',
            die_casting: 'Basınçlı Döküm',
            cnc_machining: 'CNC İşleme',
            sheet_metal: 'Sac Metal',
            '3d_printing': '3D Baskı',
            forging: 'Dövme'
        };
        return translations[process] || process;
    }

    private translateScoreKey(key: string): string {
        const translations: Record<string, string> = {
            wallThickness: 'Duvar Kalınlığı',
            draftAngles: 'Çıkış Açıları',
            undercuts: 'Undercutlar',
            holes: 'Delikler',
            fillets: 'Filletler',
            ribs: 'Nervürler',
            threads: 'Dişler',
            partingLine: 'Ayırma Çizgisi',
            symmetry: 'Simetri'
        };
        return translations[key] || key;
    }

    private getScoreColor(score: number): string {
        if (score >= 80) return '#28a745';
        if (score >= 60) return '#ffc107';
        if (score >= 40) return '#fd7e14';
        return '#dc3545';
    }

    // ════════════════════════════════════════════════════════════════════
    // SÜREÇ-SPESİFİK DFM ANALİZ METODLARİ
    // ════════════════════════════════════════════════════════════════════

    private analyzeSandCastingSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Kum döküm için uniform kesit kontrolü
        if (mfg.wallThickness) {
            const ratio = mfg.wallThickness.maxThickness / Math.max(mfg.wallThickness.minThickness, 0.1);
            if (ratio > 4) {
                issues.push({
                    id: `SC-${id++}`,
                    severity: ratio > 8 ? 'critical' : 'warning',
                    category: 'casting',
                    title: 'Kum Döküm: Kesit kalınlık oranı yüksek',
                    description: `Max/Min kesit oranı ${ratio.toFixed(1)}:1. Kum dökümde 4:1'den fazla olmamalı. Eşit olmayan soğuma çekme boşluğu (shrinkage porosity) oluşturur.`,
                    recommendation: 'Kalın bölgelere besleme kanalı (riser) ekleyin veya kesit kalınlığını eşitleyin.',
                    affectedFeatures: [],
                    estimatedCostImpact: ratio > 6 ? 25 : 15
                });
            }
        }

        // Kural 2: Büyük düz yüzeyler (porosity riski)
        const flatFaces = brep.surfaces?.planes || [];
        const largeFlatAreas = flatFaces.filter((f: any) => (f as any).area > 10000); // >100cm²
        if (largeFlatAreas.length > 0) {
            issues.push({
                id: `SC-${id++}`,
                severity: 'warning',
                category: 'casting',
                title: 'Kum Döküm: Geniş düz yüzey',
                description: `${largeFlatAreas.length} adet geniş düz yüzey tespit edildi. Kum dökümde büyük düz alanlar gözeneklilik (porosity) ve yüzey kusuru riski taşır.`,
                recommendation: 'Düz yüzeylere hafif eğim veya nervür ekleyin. Yolluk (gating) tasarımını optimize edin.',
                affectedFeatures: [],
                estimatedCostImpact: 10
            });
        }

        // Kural 3: Çekirdek (core) gerektiren iç boşluklar
        const holes = brep.features?.holes || [];
        const deepHoles = holes.filter(h => h.depth > (h.diameter / 2) * 4);
        if (deepHoles.length > 3) {
            issues.push({
                id: `SC-${id++}`,
                severity: 'warning',
                category: 'casting',
                title: 'Kum Döküm: Fazla çekirdek (core) gereksinimi',
                description: `${deepHoles.length} adet derin delik/boşluk kum çekirdeği gerektiriyor. Fazla çekirdek maliyeti ve pozisyonlama hatası riskini artırır.`,
                recommendation: 'Mümkünse derin delikleri işlemeye (CNC) bırakın. Core sayısını minimize edin.',
                affectedFeatures: [],
                estimatedCostImpact: 20
            });
        }

        return issues;
    }

    private analyzeInvestmentCastingSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Boyut limiti (hassas döküm genelde <500mm)
        const bbox = brep.geometry?.boundingBox;
        if (bbox) {
            const maxDim = Math.max(
                bbox.max.x - bbox.min.x,
                bbox.max.y - bbox.min.y,
                bbox.max.z - bbox.min.z
            );
            if (maxDim > 500) {
                issues.push({
                    id: `IC-${id++}`,
                    severity: maxDim > 1000 ? 'critical' : 'warning',
                    category: 'casting',
                    title: 'Hassas Döküm: Parça boyutu büyük',
                    description: `Maksimum boyut ${maxDim.toFixed(0)}mm. Hassas döküm genelde 500mm altı parçalar için uygundur. Büyük parçalarda seramik kabuk çatlama riski artar.`,
                    recommendation: 'Parçayı bölümlere ayırın veya kum döküm/die casting değerlendirin.',
                    affectedFeatures: [],
                    estimatedCostImpact: 30
                });
            }
        }

        // Kural 2: Çok ince duvarlar (wax kırılganlığı)
        if (mfg.wallThickness && mfg.wallThickness.minThickness < 0.8) {
            issues.push({
                id: `IC-${id++}`,
                severity: 'warning',
                category: 'casting',
                title: 'Hassas Döküm: Wax kalıp kırılganlık riski',
                description: `Minimum duvar ${mfg.wallThickness.minThickness.toFixed(2)}mm. 0.8mm altında wax kalıp üretiminde kırılma riski oluşur.`,
                recommendation: 'İnce bölgeleri en az 0.8mm yapın veya destekleyici yapılar ekleyin.',
                affectedFeatures: [],
                estimatedCostImpact: 15
            });
        }

        // Kural 3: İç köşe yarıçapı (seramik kabuk çatlağı)
        const smallFillets = (brep.features?.fillets || []).filter(f => f.radius < 0.5);
        if (smallFillets.length > 0) {
            issues.push({
                id: `IC-${id++}`,
                severity: 'info',
                category: 'casting',
                title: 'Hassas Döküm: Küçük iç köşe yarıçapları',
                description: `${smallFillets.length} adet <0.5mm yarıçaplı iç köşe. Seramik kabukta stres konsantrasyonu oluşabilir.`,
                recommendation: 'İç köşe yarıçaplarını minimum 0.5mm yapın.',
                affectedFeatures: [],
                estimatedCostImpact: 5
            });
        }

        return issues;
    }

    private analyzeCentrifugalCastingSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Simetri kontrolü
        if (mfg.symmetry && !mfg.symmetry.hasRotationalSymmetry) {
            issues.push({
                id: `CC-${id++}`,
                severity: 'critical',
                category: 'casting',
                title: 'Santrifüj Döküm: Dönel simetri yok',
                description: 'Santrifüj döküm, dönme ekseni etrafında simetrik (silindirik/halka) parçalar gerektirir. Bu parça asimetrik.',
                recommendation: 'Kum döküm veya investment casting değerlendirin. Santrifüj döküm sadece simetrik parçalar içindir.',
                affectedFeatures: [],
                estimatedCostImpact: 50
            });
        }

        // Kural 2: Et kalınlığı eşitliği (dönme dengesizliği)
        if (mfg.wallThickness) {
            const variance = mfg.wallThickness.maxThickness - mfg.wallThickness.minThickness;
            if (variance > mfg.wallThickness.avgThickness * 0.5) {
                issues.push({
                    id: `CC-${id++}`,
                    severity: 'warning',
                    category: 'casting',
                    title: 'Santrifüj Döküm: Dengesiz et kalınlığı',
                    description: `Et kalınlığı varyansı ${variance.toFixed(1)}mm. Santrifüjde eşitsiz kalınlık dönme dengesizliği ve iç gerilme yapar.`,
                    recommendation: 'Et kalınlığını dönme ekseni etrafında mümkün olduğunca eşitleyin.',
                    affectedFeatures: [],
                    estimatedCostImpact: 20
                });
            }
        }

        return issues;
    }

    private analyzeCNCTurningSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        const bbox = brep.geometry?.boundingBox;
        if (!bbox) return issues;

        const dims = [
            bbox.max.x - bbox.min.x,
            bbox.max.y - bbox.min.y,
            bbox.max.z - bbox.min.z
        ].sort((a, b) => b - a);

        const length = dims[0];
        const diameter = Math.max(dims[1], dims[2]);

        // Kural 1: L/D oranı (>10 titreşim riski)
        const ldRatio = length / Math.max(diameter, 0.1);
        if (ldRatio > 6) {
            issues.push({
                id: `CT-${id++}`,
                severity: ldRatio > 10 ? 'critical' : 'warning',
                category: 'machining',
                title: 'CNC Torna: Yüksek L/D oranı',
                description: `Uzunluk/Çap oranı ${ldRatio.toFixed(1)}:1. L/D > 6 torna işlemede titreşim ve salgı (deflection) riski oluşturur.`,
                recommendation: ldRatio > 10
                    ? 'Sabit veya hareketli punta (tailstock/steady rest) kullanın. L/D > 10 için özel bağlama gerekir.'
                    : 'Sabit punta (tailstock) kullanın. Kesme parametrelerini düşürün.',
                affectedFeatures: [],
                estimatedCostImpact: ldRatio > 10 ? 30 : 15
            });
        }

        // Kural 2: İç işleme derinliği
        const deepInternalHoles = (brep.features?.holes || []).filter(h => h.depth > (h.diameter / 2) * 8);
        if (deepInternalHoles.length > 0) {
            issues.push({
                id: `CT-${id++}`,
                severity: 'warning',
                category: 'machining',
                title: 'CNC Torna: Derin iç işleme',
                description: `${deepInternalHoles.length} adet derin iç boşluk (derinlik/çap > 4:1). İç tırnak aleti ile titreşim riski ve yüzey kalitesi düşer.`,
                recommendation: 'İç tırnak aletleri için anti-vibrasyon barlara geçin. Mümkünse derinliği azaltın.',
                affectedFeatures: [],
                estimatedCostImpact: 20
            });
        }

        // Kural 3: Dönme simetrisi
        if (mfg.symmetry && !mfg.symmetry.hasRotationalSymmetry) {
            issues.push({
                id: `CT-${id++}`,
                severity: 'info',
                category: 'machining',
                title: 'CNC Torna: Asimetrik özellikler',
                description: 'Parça tamamen dönel simetrik değil. Asimetrik özellikler canlı takım (live tooling) veya ek frezleme gerektirir.',
                recommendation: 'Turn-mill (CNC torna-freze) tezgahı kullanın veya 2. operasyonda frezeleyin.',
                affectedFeatures: [],
                estimatedCostImpact: 15
            });
        }

        return issues;
    }

    private analyzeCNC5AxisSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Alttan kesim derinliği (5-axis erişim)
        const undercutCount = mfg.undercuts?.undercuts?.length || 0;
        if (undercutCount > 5) {
            issues.push({
                id: `5X-${id++}`,
                severity: 'info',
                category: 'machining',
                title: '5-Eksen CNC: Çok sayıda alttan kesim',
                description: `${undercutCount} adet alttan kesim. 5-eksen ile işlenebilir ancak takım açıları ve CAM programlama karmaşıklığı artar.`,
                recommendation: 'CAM programında collision check yapın. Uzun takımlardan kaçınarak kısa, rijit takımlar kullanın.',
                affectedFeatures: [],
                estimatedCostImpact: 10
            });
        }

        // Kural 2: Çok derin cepler (takım boyu limiti)
        const deepPockets = (brep.features?.pockets || []).filter((p: any) => p.depth > 100);
        if (deepPockets.length > 0) {
            issues.push({
                id: `5X-${id++}`,
                severity: 'warning',
                category: 'machining',
                title: '5-Eksen CNC: Derin cepler',
                description: `${deepPockets.length} adet >100mm derin cep. Uzun takım kullanımı titreşim ve düşük yüzey kalitesi riski oluşturur.`,
                recommendation: 'Derin cepleri kademeli olarak işleyin. Takım çapını artırarak rijitliği koruyun.',
                affectedFeatures: [],
                estimatedCostImpact: 20
            });
        }

        return issues;
    }

    private analyzeEDMSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Tel erozyonda kapalı profil kontrolü
        const pockets = brep.features?.pockets || [];
        const closedPockets = pockets.filter((p: any) => p.isBlind);
        if (closedPockets.length > 0) {
            issues.push({
                id: `EDM-${id++}`,
                severity: 'info',
                category: 'machining',
                title: 'EDM: Kapalı cepler tespit edildi',
                description: `${closedPockets.length} adet kapalı cep. Tel erozyon başlangıç deliği gerektirir (ön delme operasyonu).`,
                recommendation: 'Her kapalı cep için ø1-2mm başlangıç deliği planlayın. Dalma erozyon (sinker EDM) alternatif olabilir.',
                affectedFeatures: [],
                estimatedCostImpact: 10
            });
        }

        // Kural 2: Çok ince duvarlar (erozyon termal hasar)
        if (mfg.wallThickness && mfg.wallThickness.minThickness < 0.3) {
            issues.push({
                id: `EDM-${id++}`,
                severity: 'warning',
                category: 'machining',
                title: 'EDM: İnce duvar termal hasar riski',
                description: `Minimum duvar ${mfg.wallThickness.minThickness.toFixed(2)}mm. EDM termal etkisi ince duvarlarda mikro çatlak oluşturabilir.`,
                recommendation: 'EDM parametrelerini düşürün (düşük akım/voltaj). İnce bölgelerde finishing pass ekleyin.',
                affectedFeatures: [],
                estimatedCostImpact: 15
            });
        }

        return issues;
    }

    private analyzeWeldingSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Et kalınlığı farkı (kaynak dikişi stres konsantrasyonu)
        if (mfg.wallThickness) {
            const ratio = mfg.wallThickness.maxThickness / Math.max(mfg.wallThickness.minThickness, 0.1);
            if (ratio > 3) {
                issues.push({
                    id: `WLD-${id++}`,
                    severity: ratio > 5 ? 'critical' : 'warning',
                    category: 'welding',
                    title: 'Kaynak: Farklı et kalınlıkları birleşimi',
                    description: `Et kalınlığı oranı ${ratio.toFixed(1)}:1. Farklı kalınlıklarda kaynak distorsiyon ve stres konsantrasyonu oluşturur.`,
                    recommendation: 'İnce/kalın geçişlerde kademeli konik (taper) geçiş yapın. Ön ısıtma uygulayın.',
                    affectedFeatures: [],
                    estimatedCostImpact: 20
                });
            }
        }

        // Kural 2: Dar köşelerde kaynak erişimi
        const smallFillets = (brep.features?.fillets || []).filter(f => f.radius < 3);
        if (smallFillets.length > 5) {
            issues.push({
                id: `WLD-${id++}`,
                severity: 'warning',
                category: 'welding',
                title: 'Kaynak: Dar köşe erişim sorunu',
                description: `${smallFillets.length} adet <3mm yarıçaplı köşe. Kaynak torçu erişimi zorlaşır ve eksik nüfuziyet riski oluşur.`,
                recommendation: 'Kaynak bölgelerinde minimum 5mm iç yarıçap bırakın. Robot kaynak için erişim açılarını kontrol edin.',
                affectedFeatures: [],
                estimatedCostImpact: 15
            });
        }

        // Kural 3: Toplam dikiş uzunluğu → distorsiyon tahmini
        const totalEdgeLength = brep.geometry?.totalEdgeLength || 0;
        if (totalEdgeLength > 5000) { // >5m toplam kenar
            issues.push({
                id: `WLD-${id++}`,
                severity: 'info',
                category: 'welding',
                title: 'Kaynak: Yüksek toplam dikiş uzunluğu',
                description: `Tahmini toplam kenar uzunluğu ${(totalEdgeLength / 1000).toFixed(1)}m. Uzun kaynak dikişleri termal distorsiyon riski taşır.`,
                recommendation: 'Kaynak sırasını optimize edin (simetrik kaynak). Ara gerilme giderme (stress relief) planlayın.',
                affectedFeatures: [],
                estimatedCostImpact: 10
            });
        }

        return issues;
    }

    private analyzeHeatTreatmentSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Kesit kalınlığı varyansı (çarpılma)
        if (mfg.wallThickness) {
            const variance = mfg.wallThickness.maxThickness - mfg.wallThickness.minThickness;
            if (variance > mfg.wallThickness.avgThickness * 0.8) {
                issues.push({
                    id: `HT-${id++}`,
                    severity: 'warning',
                    category: 'heat_treatment',
                    title: 'Isıl İşlem: Eşitsiz kesit kalınlığı - çarpılma riski',
                    description: `Kesit kalınlığı varyansı ${variance.toFixed(1)}mm (ort: ${mfg.wallThickness.avgThickness.toFixed(1)}mm). Eşitsiz kesitler farklı soğuma hızı nedeniyle çarpılma yapar.`,
                    recommendation: 'Kontrollü soğutma (yavaş soğutma veya temperleme) uygulayın. Kritik yüzeyleri işleme payı ile bırakın.',
                    affectedFeatures: [],
                    estimatedCostImpact: 15
                });
            }
        }

        // Kural 2: Keskin köşeler (stres yoğunlaşma → çatlak)
        const sharpEdgeCount = brep.geometry?.sharpEdges || 0;

        if (sharpEdgeCount > 3) {
            issues.push({
                id: `HT-${id++}`,
                severity: 'warning',
                category: 'heat_treatment',
                title: 'Isıl İşlem: Stres yoğunlaşma noktaları',
                description: `${sharpEdgeCount} adet keskin köşe. Sertleştirme (quenching) sırasında bu noktalarda çatlak oluşma riski yüksektir.`,
                recommendation: 'Keskin köşelere minimum R1mm fillet ekleyin. Sertleştirme öncesi gerilme giderme uygulayın.',
                affectedFeatures: [],
                estimatedCostImpact: 10
            });
        }

        // Kural 3: Derin delikler (sertlik ulaşımı)
        const deepHoles = (brep.features?.holes || []).filter(h => h.depth > 50);
        if (deepHoles.length > 0) {
            issues.push({
                id: `HT-${id++}`,
                severity: 'info',
                category: 'heat_treatment',
                title: 'Isıl İşlem: Derin deliklerde sertlik dağılımı',
                description: `${deepHoles.length} adet >50mm derin delik. Yüzey sertleştirmede (case hardening) derin deliklerin iç yüzeylerine sertlik ulaşmayabilir.`,
                recommendation: 'Derin deliklerin iç yüzeylerinde sertlik kontrolü planlayın. Gerekirse indüksiyon sertleştirme kullanın.',
                affectedFeatures: [],
                estimatedCostImpact: 5
            });
        }

        return issues;
    }

    private analyzeSurfaceTreatmentSpecific(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Kural 1: Derin cep/boşluklarda kaplama penetrasyonu
        const deepPockets = (brep.features?.pockets || []).filter((p: any) => p.depth > 30);
        if (deepPockets.length > 0) {
            issues.push({
                id: `ST-${id++}`,
                severity: 'warning',
                category: 'surface_treatment',
                title: 'Yüzey İşlem: Derin boşluklarda kaplama erişimi',
                description: `${deepPockets.length} adet >30mm derin cep/boşluk. Anodizasyon/galvanik kaplama derin boşlukların dibine eşit ulaşmayabilir.`,
                recommendation: 'Derin boşluklar için özel fikstür veya iç elektrot kullanın. Sprey kaplama alternatif olabilir.',
                affectedFeatures: [],
                estimatedCostImpact: 15
            });
        }

        // Kural 2: İç dişler (maskeleme gereksinimi)
        const threadCount = mfg.threads?.threads?.length || 0;
        if (threadCount > 0) {
            issues.push({
                id: `ST-${id++}`,
                severity: 'info',
                category: 'surface_treatment',
                title: 'Yüzey İşlem: Diş bölgeleri maskeleme',
                description: `${threadCount} adet dişli bölge. Kaplama kalınlığı diş toleransını bozabilir - maskeleme gerekir.`,
                recommendation: 'Dişli bölgeleri silikon tapa veya özel maskeleme bandı ile kapatın. Kaplama sonrası tolerans kontrolü yapın.',
                affectedFeatures: [],
                estimatedCostImpact: 10
            });
        }

        // Kural 3: Keskin kenarlar (kaplama birikmesi)
        const chamfers = brep.features?.chamfers || [];
        if (chamfers.length < 3 && (brep.topology?.edgeCount || 0) > 20) {
            issues.push({
                id: `ST-${id++}`,
                severity: 'info',
                category: 'surface_treatment',
                title: 'Yüzey İşlem: Keskin kenarlarda kaplama birikmesi',
                description: 'Çok sayıda pah kırılmamış keskin kenar. Galvanik kaplamada keskin kenarlarda aşırı birikme ("dog bone" efekti) oluşur.',
                recommendation: 'Kaplama öncesi tüm keskin kenarlara minimum 0.3mm pah veya R0.3mm fillet ekleyin.',
                affectedFeatures: [],
                estimatedCostImpact: 5
            });
        }

        return issues;
    }

    // ════════════════════════════════════════════════════════
    // PROCESS-GEOMETRY FITNESS (EN KRİTİK KONTROL)
    // Parça geometrisinin seçilen üretim prosesiyle uyumunu kontrol eder.
    // Yanlış proses seçiminde tüm skoru düşürür.
    // ════════════════════════════════════════════════════════

    private analyzeProcessGeometryFitness(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;
        const CAT = 'Process Geometry Fit';

        // ── Geometri metriklerini hesapla ──
        const bb = brep.geometry?.boundingBox;
        if (!bb) return issues;

        const dimX = bb.max.x - bb.min.x;
        const dimY = bb.max.y - bb.min.y;
        const dimZ = bb.max.z - bb.min.z;
        const dims = [dimX, dimY, dimZ].sort((a, b) => b - a); // büyükten küçüğe
        const maxDim = dims[0];
        const midDim = dims[1];
        const minDim = dims[2];

        // Yüzey dağılımı
        const totalSurfaceArea = brep.geometry?.surfaceArea || 1;
        const cylinderArea = (brep.surfaces?.cylinders || []).reduce((s, c) => s + c.area, 0);
        const planeArea = (brep.surfaces?.planes || []).reduce((s, c) => s + c.area, 0);
        const sphereArea = (brep.surfaces?.spheres || []).reduce((s, c) => s + c.area, 0);
        const coneArea = (brep.surfaces?.cones || []).reduce((s, c) => s + c.area, 0);
        const freeformArea = (brep.surfaces?.freeform || []).reduce((s, c) => s + c.area, 0);
        const nurbsArea = (brep.surfaces?.nurbs || []).reduce((s, c) => s + c.area, 0);
        const revolutionArea = (brep.surfaces?.revolutions || []).reduce((s, c) => s + c.area, 0);

        const cylinderRatio = cylinderArea / totalSurfaceArea;
        const planeRatio = planeArea / totalSurfaceArea;
        const rotationalRatio = (cylinderArea + coneArea + sphereArea + revolutionArea) / totalSurfaceArea;
        const freeformRatio = (freeformArea + nurbsArea) / totalSurfaceArea;

        // Simetri
        const hasRotSymmetry = mfg.symmetry?.hasRotationalSymmetry || false;

        // En boy oranları
        const aspectRatio = maxDim / Math.max(minDim, 0.01);
        const flatnessRatio = minDim / Math.max(midDim, 0.01); // 0'a yakınsa düz

        // Feature sayıları
        const holeCount = brep.features?.holes?.length || 0;
        const pocketCount = brep.features?.pockets?.length || 0;

        // Hacim
        const volume = brep.geometry?.volume || 0;

        // Duvar kalınlığı
        const avgWall = mfg.wallThickness?.avgThickness || 0;
        const minWall = mfg.wallThickness?.minThickness || 0;

        // ════════════════════════════════════════════════════
        // CNC TORNA (cnc_turning) - DÖNEL SİMETRİ GEREKTİRİR
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'cnc_turning') {
            // Torna parçası rotasyonel simetriye sahip olmalı
            // Silindirik yüzeyler baskın olmalı

            if (!hasRotSymmetry && rotationalRatio < 0.4) {
                // Kesinlikle torna parçası DEĞİL
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'PROSES UYUMSUZLUK: Parça CNC torna için uygun değil',
                    description: `Parça dönel simetri göstermiyor. Silindirik yüzey oranı: %${(rotationalRatio * 100).toFixed(0)} (min %40 gerekli). CNC torna sadece dönel simetrili (silindirik, konik) parçalar üretebilir.`,
                    affectedFeatures: [],
                    recommendation: 'Bu parça için CNC frezeleme (3-eksen veya 5-eksen), döküm veya enjeksiyon kalıplama değerlendirin. Torna sadece mil, burç, pim gibi dönel parçalar içindir.',
                    estimatedCostImpact: 200,
                    autofix: false,
                    designSuggestion: 'Farklı bir üretim prosesi seçin.'
                });
            } else if (rotationalRatio < 0.6) {
                // Kısmen dönel ama ideal değil
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Torna Uyumu: Düşük dönel yüzey oranı',
                    description: `Silindirik/konik yüzey oranı: %${(rotationalRatio * 100).toFixed(0)}. İdeal torna parçasında >%60 olmalı. Parçanın bazı bölgeleri ek frezeleme gerektirebilir (turn-mill).`,
                    affectedFeatures: [],
                    recommendation: 'Turn-mill (torna-freze kombine) tezgahı veya torna sonrası 2. operasyon frezeleme planlayın.',
                    estimatedCostImpact: 50,
                    autofix: false
                });
            }

            // Torna parçalarında en-boy oranı kontrolü (çok uzun = titreşim)
            if (aspectRatio > 10) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Torna: Yüksek L/D oranı (titreşim riski)',
                    description: `En boy oranı: ${aspectRatio.toFixed(1)}:1. L/D > 10 punta desteği veya sabit bağlama gerektirir.`,
                    affectedFeatures: [],
                    recommendation: 'Punta (tailstock) desteği kullanın. L/D > 12 için steady rest (sabit mesnet) ekleyin.',
                    estimatedCostImpact: 25,
                    autofix: false
                });
            }

            // Çok sayıda cep/boşluk torna için uygun değil
            if (pocketCount > 2) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Torna: Çok sayıda cep/boşluk',
                    description: `${pocketCount} adet cep/boşluk. Torna operasyonuyla üretimi zor - ek frezeleme operasyonu gerektirir.`,
                    affectedFeatures: [],
                    recommendation: 'Cepleri azaltın veya CNC frezeleme prosesine geçin.',
                    estimatedCostImpact: 40,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // SAC METAL (sheet_metal) - İNCE, DÜZ GEOMETRİ GEREKTİRİR
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'sheet_metal') {
            // Sac metal parçaları ince ve düz olmalı
            // Baskın yüzeyler düzlem olmalı
            // Bir boyut diğerlerinden çok küçük olmalı (kalınlık)

            if (flatnessRatio > 0.3 && planeRatio < 0.3) {
                // Hacimsel parça - sac metale uygun değil
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'PROSES UYUMSUZLUK: Parça sac metal için uygun değil',
                    description: `Parça hacimsel (düzlük oranı: ${flatnessRatio.toFixed(2)}, düzlem oranı: %${(planeRatio * 100).toFixed(0)}). Sac metal sadece ince, düz veya bükülmüş parçalar üretebilir. En ince boyut/orta boyut oranı 0.3'ten küçük olmalı.`,
                    affectedFeatures: [],
                    recommendation: 'Bu parça için CNC frezeleme, döküm veya enjeksiyon kalıplama değerlendirin. Sac metal sadece levha kesme, bükme ve form verme yapabilir.',
                    estimatedCostImpact: 200,
                    autofix: false,
                    designSuggestion: 'Farklı bir üretim prosesi seçin.'
                });
            } else if (flatnessRatio > 0.15) {
                // Biraz kalın ama yine de sac olabilir
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Sac Metal: Kalın profil',
                    description: `Kalınlık/genişlik oranı: ${flatnessRatio.toFixed(2)}. İdeal sac metal parçasında <0.15 olmalı. Parça standart sac kalınlıklarından (0.5-6mm) kalın olabilir.`,
                    affectedFeatures: [],
                    recommendation: 'Sac kalınlığını standart değerlere (0.5, 0.8, 1.0, 1.2, 1.5, 2.0, 3.0, 4.0, 5.0, 6.0mm) ayarlayın.',
                    estimatedCostImpact: 30,
                    autofix: false
                });
            }

            // Sac metalde derin cep olamaz
            if (pocketCount > 0) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'Sac Metal: Cep/boşluk mevcut',
                    description: `${pocketCount} adet cep/boşluk. Sac metal prosesinde derin cep/boşluk üretilemez. Sadece kesme, bükme ve form verme mümkündür.`,
                    affectedFeatures: [],
                    recommendation: 'Cepleri kaldırın veya CNC frezeleme prosesine geçin. Form verme (stamping) ile sığ kabartmalar mümkündür.',
                    estimatedCostImpact: 150,
                    autofix: false,
                    designSuggestion: 'Cepleri tasarımdan çıkarın.'
                });
            }

            // Sac metalde çok sayıda silindirik yüzey (patron, nervür) uyumsuz
            if (cylinderRatio > 0.4 && !hasRotSymmetry) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Sac Metal: Yüksek silindirik yüzey oranı',
                    description: `Silindirik yüzey oranı: %${(cylinderRatio * 100).toFixed(0)}. Bükme yarıçapları dışında silindirik formlar sac metalde zordur.`,
                    affectedFeatures: [],
                    recommendation: 'Silindirik formları azaltın. Boru şeklindeki bölümler için ayrı haddelenmiş boru kullanıp kaynak yapın.',
                    estimatedCostImpact: 40,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // ENJEKSİYON KALIPLAMA (injection_molding) - KABUK GEOMETRİ
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'injection_molding') {
            // Enjeksiyon kalıplama duvar kalınlığına duyarlı
            // Çok kalın masif parçalar uygun değil (sink mark, warpage)

            if (avgWall > 8) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'Enjeksiyon: Aşırı kalın duvar',
                    description: `Ortalama duvar kalınlığı: ${avgWall.toFixed(1)}mm. Enjeksiyon kalıplamada max 6mm önerilir. Kalın bölgeler çökme izi, iç boşluk ve çarpılma yaratır.`,
                    affectedFeatures: [],
                    recommendation: 'Duvar kalınlığını 1.5-4mm arasına düşürün. Kalın bölgeleri nervür yapısına dönüştürün (coring out). Alternatif: yapısal köpük enjeksiyon.',
                    estimatedCostImpact: 80,
                    autofix: false,
                    designSuggestion: `Duvar kalınlığını ${avgWall.toFixed(1)}mm'den 2.5-3.5mm'ye düşürün, nervürlerle destekleyin.`
                });
            }

            // Çok büyük parça kontrolü (tipik enjeksiyon makinesi limitleri)
            if (maxDim > 1000) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'Enjeksiyon: Parça boyutu çok büyük',
                    description: `Maksimum boyut: ${maxDim.toFixed(0)}mm. Standart enjeksiyon makineleri 300-800mm arasında çalışır. >1000mm için özel büyük tonajlı makine gerekir.`,
                    affectedFeatures: [],
                    recommendation: 'Parçayı küçültün veya alt-montajlara bölün. >1000mm parçalar için 2000+ ton makine ve çok büyük kalıp gerekir (maliyet 5-10x artar).',
                    estimatedCostImpact: 150,
                    autofix: false
                });
            }

            // Tamamen freeform parça (çok karmaşık kalıp)
            if (freeformRatio > 0.6) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Enjeksiyon: Yüksek serbest form yüzey oranı',
                    description: `Serbest form yüzey oranı: %${(freeformRatio * 100).toFixed(0)}. Karmaşık yüzeyler kalıp işleme maliyetini artırır (5-eksen CNC veya EDM gerektirir).`,
                    affectedFeatures: [],
                    recommendation: 'Mümkünse yüzeyleri basitleştirin. Kritik yüzeyleri A-class, geri kalanını ruled surface yapın.',
                    estimatedCostImpact: 40,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // BASINCLI DÖKÜM (die_casting) - ORTALAMABoyut + İNCE DUVAR
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'die_casting') {
            if (avgWall > 6) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Basınçlı Döküm: Kalın duvar',
                    description: `Ortalama duvar: ${avgWall.toFixed(1)}mm. Basınçlı dökümde ideal: 1-4mm. Kalın bölgelerde porozite riski yüksek.`,
                    affectedFeatures: [],
                    recommendation: 'Duvar kalınlığını 1.5-3.5mm arasına düşürün. Kalın bölgeleri nervürle destekleyin.',
                    estimatedCostImpact: 60,
                    autofix: false
                });
            }

            // Undercut kontrolü (die casting'de undercut çok maliyetli - slider gerektirir)
            const undercutCount = mfg.undercuts?.undercuts?.length || 0;
            if (undercutCount > 4) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'Basınçlı Döküm: Çok sayıda altı kesim',
                    description: `${undercutCount} adet undercut. Her undercut için kalıpta ayrı slider/core gerekir (birim maliyet +$2000-10000).`,
                    affectedFeatures: [],
                    recommendation: 'Undercut sayısını azaltın. Bazı undercut\'ları post-machining ile oluşturun.',
                    estimatedCostImpact: 100,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // CNC FREZELEME 3-EKSEN (cnc_machining)
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'cnc_machining') {
            // 3-eksen CNC iç boşluklara erişemez
            const deepPockets = (brep.features?.pockets || []).filter((p: any) => p.depth > maxDim * 0.5);
            if (deepPockets.length > 3) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'CNC 3-Eksen: Derin cep erişim sorunu',
                    description: `${deepPockets.length} adet derin cep (derinlik > boyutun %50'si). 3-eksen CNC'de uzun takım gerektirir, titreşim ve yüzey kalitesi sorunları olur.`,
                    affectedFeatures: [],
                    recommendation: 'Cep derinliğini azaltın veya 5-eksen CNC kullanın. Takım boyu/çapı oranı 4:1\'i geçmemeli.',
                    estimatedCostImpact: 30,
                    autofix: false
                });
            }

            // Çok ince duvarlar CNC'de zor (titreşim)
            if (minWall > 0 && minWall < 0.8) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'CNC: İnce duvar titreşimi',
                    description: `Min duvar kalınlığı: ${minWall.toFixed(2)}mm. <1mm duvarlar frezelemede titreşir ve kırılabilir.`,
                    affectedFeatures: [],
                    recommendation: 'Minimum duvar kalınlığını 1mm üzerine çıkarın. İnce bölgelere destek nervürleri ekleyin.',
                    estimatedCostImpact: 20,
                    autofix: false,
                    designSuggestion: `Min duvar kalınlığını ${minWall.toFixed(2)}mm'den 1.0mm'ye artırın.`
                });
            }
        }

        // ════════════════════════════════════════════════════
        // CNC 5-EKSEN (cnc_5axis) - ÇOK ESNEK AMA MALİYETLİ
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'cnc_5axis') {
            // 5-eksen çok esnek, ama gereksiz kullanım maliyet artırır
            if (planeRatio > 0.8 && pocketCount < 3 && holeCount < 5) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'info',
                    category: CAT,
                    title: '5-Eksen: Basit geometri - 3-eksen yeterli',
                    description: `Parça büyük oranda düz yüzeylerden oluşuyor (%${(planeRatio * 100).toFixed(0)}). 3-eksen CNC ile daha ekonomik üretim mümkün.`,
                    affectedFeatures: [],
                    recommendation: 'Maliyet düşürmek için 3-eksen CNC frezeleme değerlendirin. 5-eksen sadece karmaşık eğimli yüzeyler için gerekli.',
                    estimatedCostImpact: -20,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // DÖVME (forging) - BASİT, KOMPAKTGEOMETRİ
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'forging') {
            // Dövme karmaşık geometrilere uygun değil
            if (pocketCount > 1 || holeCount > 3) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'PROSES UYUMSUZLUK: Dövme için çok karmaşık geometri',
                    description: `${pocketCount} cep, ${holeCount} delik. Dövme basit, kompakt formlar üretir. Derin cepler ve çok sayıda delik dövmeyle üretilemez.`,
                    affectedFeatures: [],
                    recommendation: 'Bu parça için CNC frezeleme veya döküm değerlendirin. Dövme sonrası delik ve cepler CNC ile açılabilir (forging + machining).',
                    estimatedCostImpact: 150,
                    autofix: false,
                    designSuggestion: 'Dövme ile sadece ana formu üretin, detay özellikler için post-machining planlayın.'
                });
            }

            // İnce duvarlar dövmeyle zor
            if (minWall > 0 && minWall < 3) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Dövme: İnce bölgeler',
                    description: `Min duvar: ${minWall.toFixed(1)}mm. Dövmede minimum 3mm duvar kalınlığı gerekir (metal akışı ve kalıp dolumu).`,
                    affectedFeatures: [],
                    recommendation: 'İnce bölgeleri kalınlaştırın. Dövme sonrası CNC ile ince bölgeleri işleyin.',
                    estimatedCostImpact: 40,
                    autofix: false
                });
            }

            // Çok yüksek aspect ratio dövmede zor
            if (aspectRatio > 6) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Dövme: Yüksek en-boy oranı',
                    description: `En boy oranı: ${aspectRatio.toFixed(1)}:1. Dövmede >6:1 oran kalıp dolumu sorunları yaratır.`,
                    affectedFeatures: [],
                    recommendation: 'Parçayı daha kompakt hale getirin veya roll forging (silindirik dövme) değerlendirin.',
                    estimatedCostImpact: 35,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // KUM DÖKÜM (sand_casting) - ORTA KARMAŞIKLIK
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'sand_casting') {
            // Çok ince duvarlar kum dökümde dolmaz
            if (minWall > 0 && minWall < 4) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Kum Döküm: İnce duvar dolum riski',
                    description: `Min duvar: ${minWall.toFixed(1)}mm. Kum dökümde minimum 4mm (çelik) / 3mm (alüminyum) duvar kalınlığı gerekir.`,
                    affectedFeatures: [],
                    recommendation: 'Duvar kalınlığını artırın. İnce bölgeleri döküm sonrası CNC ile işleyin.',
                    estimatedCostImpact: 30,
                    autofix: false,
                    designSuggestion: `Min duvar kalınlığını ${minWall.toFixed(1)}mm'den 4.0mm'ye artırın.`
                });
            }

            // Çok hassas toleranslı küçük delikleri döküm yapamaz
            const smallHoles = (brep.features?.holes || []).filter((h: any) => (h.diameter || h.radius * 2) < 6);
            if (smallHoles.length > 3) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'info',
                    category: CAT,
                    title: 'Kum Döküm: Küçük delikler',
                    description: `${smallHoles.length} adet <6mm delik. Küçük delikler kum dökümde üretilemez, post-machining gerekir.`,
                    affectedFeatures: [],
                    recommendation: 'Küçük delikleri döküm sonrası CNC ile açın. Kalıpta sadece >10mm deliklere maça koyun.',
                    estimatedCostImpact: 15,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // HASSAS DÖKÜM (investment_casting) - İNCE, KARMAŞIK İÇİN İYİ
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'investment_casting') {
            // Boyut limiti - hassas döküm küçük-orta parçalar için
            if (maxDim > 600) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Hassas Döküm: Büyük parça',
                    description: `Maks boyut: ${maxDim.toFixed(0)}mm. Hassas döküm (investment casting) tipik olarak <500mm parçalar için. Büyük parçalarda mum modeli deformasyonu riski.`,
                    affectedFeatures: [],
                    recommendation: 'Parçayı bölün veya kum döküm prosesine geçin.',
                    estimatedCostImpact: 50,
                    autofix: false
                });
            }

            // Ağırlık kontrolü (tipik max ~50kg)
            const density = 0.00785; // steel kg/cm³
            const estWeight = volume * density;
            if (estWeight > 50) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Hassas Döküm: Ağır parça',
                    description: `Tahmini ağırlık: ${estWeight.toFixed(1)}kg. Hassas döküm tipik olarak <50kg. Ağır parçalarda kalıp ve fırın boyutu sınırlayıcı.`,
                    affectedFeatures: [],
                    recommendation: 'Kum döküm veya basınçlı döküm değerlendirin.',
                    estimatedCostImpact: 60,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // SANTRİFÜJ DÖKÜM (centrifugal_casting) - SİLİNDİRİK GEREKTİRİR
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'centrifugal_casting') {
            if (!hasRotSymmetry || rotationalRatio < 0.5) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'critical',
                    category: CAT,
                    title: 'PROSES UYUMSUZLUK: Santrifüj döküm için dönel geometri gerekli',
                    description: `Parça dönel simetri göstermiyor (silindirik yüzey oranı: %${(rotationalRatio * 100).toFixed(0)}). Santrifüj döküm sadece boru, halka, burç gibi dönel parçalar üretir.`,
                    affectedFeatures: [],
                    recommendation: 'Bu parça için kum döküm, hassas döküm veya basınçlı döküm değerlendirin.',
                    estimatedCostImpact: 200,
                    autofix: false,
                    designSuggestion: 'Farklı döküm prosesi seçin.'
                });
            }
        }

        // ════════════════════════════════════════════════════
        // 3D BASKI (3d_printing) - EN ESNEK AMA BOYUT LİMİTLİ
        // ════════════════════════════════════════════════════
        if (this.targetProcess === '3d_printing') {
            // Boyut kontrolü (tipik FDM: 300x300x400mm, SLS: 300x300x300mm)
            if (maxDim > 400) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: '3D Baskı: Büyük yapı hacmi',
                    description: `Maks boyut: ${maxDim.toFixed(0)}mm. Çoğu 3D yazıcı 300x300x400mm yapı hacmine sahip. Büyük parçalar bölünme veya endüstriyel yazıcı gerektirir.`,
                    affectedFeatures: [],
                    recommendation: 'Parçayı bölün veya endüstriyel SLS/DMLS yazıcı kullanın (build volume 600mm+).',
                    estimatedCostImpact: 40,
                    autofix: false
                });
            }

            // Çok ince detaylar
            if (minWall > 0 && minWall < 0.4) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: '3D Baskı: Çok ince duvar',
                    description: `Min duvar: ${minWall.toFixed(2)}mm. FDM için min 0.8mm, SLA/SLS için min 0.4mm gerekir.`,
                    affectedFeatures: [],
                    recommendation: 'Duvar kalınlığını artırın. FDM: min 0.8mm, SLA: min 0.4mm, SLS: min 0.6mm.',
                    estimatedCostImpact: 15,
                    autofix: false,
                    designSuggestion: `Min duvar kalınlığını ${minWall.toFixed(2)}mm'den 0.8mm'ye artırın.`
                });
            }

            // Büyük düz yüzeyler warpage riski
            if (planeRatio > 0.7 && maxDim > 150) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'info',
                    category: CAT,
                    title: '3D Baskı: Düz yüzey çarpılma riski',
                    description: `Büyük düz yüzeyler (%${(planeRatio * 100).toFixed(0)}) baskı sırasında çarpılma (warpage) riski taşır.`,
                    affectedFeatures: [],
                    recommendation: 'Isıtmalı tabla kullanın. Brim/raft ekleyin. Parça oryantasyonunu optimize edin.',
                    estimatedCostImpact: 10,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // CNC EDM (cnc_edm) - ÖZEL AMAÇLI
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'cnc_edm') {
            // EDM sadece iletken malzemeler ve özel geometriler için
            // Çok basit parçalarda EDM gereksiz pahalı
            if (planeRatio > 0.7 && holeCount < 2 && pocketCount < 2) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'EDM: Basit geometri - CNC frezeleme yeterli',
                    description: 'Parça büyük oranda düz yüzeylerden oluşuyor. EDM genelde dar slot, karmaşık iç profil ve sert malzeme işleme için gerekli.',
                    affectedFeatures: [],
                    recommendation: 'Maliyet düşürmek için CNC frezeleme değerlendirin. EDM saatlik ücreti CNC\'nin 2-3 katıdır.',
                    estimatedCostImpact: -30,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // CNC TAŞLAMA (cnc_grinding) - DÜZ/SİLİNDİRİK YÜZEY
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'cnc_grinding') {
            // Taşlama düz ve silindirik yüzeyler için
            if (freeformRatio > 0.3) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Taşlama: Serbest form yüzeyler',
                    description: `Serbest form yüzey oranı: %${(freeformRatio * 100).toFixed(0)}. Taşlama düz (yüzey taşlama) ve silindirik yüzeyler (silindirik taşlama) için uygundur.`,
                    affectedFeatures: [],
                    recommendation: 'Serbest form yüzeyler için CNC frezeleme + polishing kullanın. Taşlama sadece hassas toleranslı düz/silindirik yüzeyler için.',
                    estimatedCostImpact: 40,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // KABUK DÖKÜM (shell_mold_casting)
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'shell_mold_casting') {
            if (minWall > 0 && minWall < 3) {
                issues.push({
                    id: `PGF-${id++}`,
                    severity: 'warning',
                    category: CAT,
                    title: 'Kabuk Döküm: İnce duvar',
                    description: `Min duvar: ${minWall.toFixed(1)}mm. Kabuk dökümde minimum 3mm duvar kalınlığı gerekir.`,
                    affectedFeatures: [],
                    recommendation: 'Duvar kalınlığını artırın veya hassas döküm (investment casting) değerlendirin.',
                    estimatedCostImpact: 25,
                    autofix: false
                });
            }
        }

        // ════════════════════════════════════════════════════
        // KAYNAK, ISIL İŞLEM, YÜZEY İŞLEM - GEOMETRİ BAĞIMSIZ
        // ════════════════════════════════════════════════════
        if (this.targetProcess === 'welding' || this.targetProcess === 'heat_treatment' || this.targetProcess === 'surface_treatment') {
            // Bu prosesler geometriden bağımsız, her zaman uyumlu
            issues.push({
                id: `PGF-${id++}`,
                severity: 'pass',
                category: CAT,
                title: `${this.targetProcess}: Geometri uyumluluğu iyi`,
                description: `${this.targetProcess} prosesi geometriden bağımsızdır, tüm parça formlarına uygulanabilir.`,
                affectedFeatures: [],
                recommendation: 'Ek geometri kısıtlaması yok.'
            });
        }

        // ════════════════════════════════════════════════════
        // GENEL: Eğer hiç issue oluşmadıysa → parça prosesle uyumlu
        // ════════════════════════════════════════════════════
        if (issues.length === 0) {
            issues.push({
                id: `PGF-${id++}`,
                severity: 'pass',
                category: CAT,
                title: `${this.targetProcess}: Geometri uyumluluğu iyi`,
                description: `Parça geometrisi ${this.targetProcess} prosesiyle genel olarak uyumlu.`,
                affectedFeatures: [],
                recommendation: 'Geometri-proses uyumu yeterli seviyede.'
            });
        }

        return issues;
    }

    // ════════════════════════════════════════════════════════
    // PRODUCT-READY: YENI GENEL ANALIZLER
    // ════════════════════════════════════════════════════════

    /**
     * Cooling Channel Analysis - Enjeksiyon kalıplama soğutma kanalı analizi
     * Duvar kalınlığı ile soğutma kanalı mesafesi optimizasyonu
     */
    private analyzeCoolingChannels(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece injection_molding ve die_casting için
        if (this.targetProcess !== 'injection_molding' && this.targetProcess !== 'die_casting') {
            return issues;
        }

        if (!mfg.wallThickness) {
            return issues;
        }

        // Kural 1: Uniform wall thickness kontrolü (soğutma kanalı mesafesi 2x-3x duvar kalınlığı olmalı)
        const avgThickness = mfg.wallThickness.avgThickness;
        const thicknessVariance = mfg.wallThickness.maxThickness - mfg.wallThickness.minThickness;

        if (thicknessVariance > avgThickness * 0.5) {
            issues.push({
                id: `COOL-${id++}`,
                severity: 'warning',
                category: 'cooling_channels',
                title: 'Soğutma Kanalı: Eşit olmayan duvar kalınlığı',
                description: `Duvar kalınlığı varyansı ${thicknessVariance.toFixed(1)}mm (ort: ${avgThickness.toFixed(1)}mm). Eşit olmayan duvar kalınlıkları farklı soğuma hızları nedeniyle çarpılmaya neden olur. Soğutma kanalı yerleşimi daha karmaşık olacaktır.`,
                affectedFeatures: [],
                recommendation: `Duvar kalınlığını uniform tutun. Soğutma kanalı mesafesini 2x-3x duvar kalınlığı (${(avgThickness * 2).toFixed(1)}-${(avgThickness * 3).toFixed(1)}mm) olarak planlayın. Kalın bölgelere ekstra soğutma kanalı ekleyin.`,
                estimatedCostImpact: 20,
                relatedStandard: 'ISO 12890',
                autofix: false,
                designSuggestion: `Duvar kalınlığını ${avgThickness.toFixed(1)}mm ±0.5mm tolerans ile uniform yapın.`
            });
        }

        // Kural 2: Thick sections (kalın bölgeler) ekstra soğutma gerektirir
        const thickRegions = mfg.wallThickness.thickWallRegions || [];
        if (thickRegions.length > 0) {
            const maxThick = Math.max(...thickRegions.map(r => r.thickness));
            if (maxThick > avgThickness * 1.5) {
                issues.push({
                    id: `COOL-${id++}`,
                    severity: 'warning',
                    category: 'cooling_channels',
                    title: 'Soğutma Kanalı: Kalın bölgelerde yetersiz soğuma riski',
                    description: `${thickRegions.length} adet kalın bölge tespit edildi (max: ${maxThick.toFixed(1)}mm). Kalın bölgeler yavaş soğur ve çökme (sink mark) oluşur.`,
                    affectedFeatures: thickRegions.flatMap(r => r.faceIds),
                    recommendation: `Kalın bölgelere baffle veya bubblers ile ekstra soğutma kanalı ekleyin. Alternatif olarak coring uygulayarak duvar kalınlığını azaltın.`,
                    estimatedCostImpact: 25,
                    relatedStandard: 'ISO 294-4',
                    autofix: false,
                    designSuggestion: `Kalın bölgeleri coring ile ${avgThickness.toFixed(1)}mm duvar kalınlığına düşürün veya soğutma kanalı ekleyin.`
                });
            }
        }

        // Kural 3: Part boyutu vs soğutma süresi (cycle time)
        const volume = brep.geometry?.volume || 0;
        const surfaceArea = brep.geometry?.surfaceArea || 0;
        if (volume > 0 && surfaceArea > 0) {
            const volumeToSurfaceRatio = volume / surfaceArea; // mm cinsinden karakteristik kalınlık
            if (volumeToSurfaceRatio > avgThickness * 2) {
                issues.push({
                    id: `COOL-${id++}`,
                    severity: 'info',
                    category: 'cooling_channels',
                    title: 'Soğutma Kanalı: Uzun soğuma süresi tahmini',
                    description: `Parça hacim/yüzey oranı ${volumeToSurfaceRatio.toFixed(1)}mm. Kalın/büyük parçalar uzun soğuma süresi (cycle time) gerektirir.`,
                    affectedFeatures: [],
                    recommendation: 'Cycle time optimizasyonu için konformal cooling veya 3D-printed mold inserts kullanmayı düşünün. Soğutma kanallarını partaya mümkün olduğunca yakın (2x duvar kalınlığı mesafe) yerleştirin.',
                    estimatedCostImpact: 15,
                    autofix: false
                });
            }
        }

        if (issues.length === 0) {
            issues.push({
                id: `COOL-${id++}`,
                severity: 'pass',
                category: 'cooling_channels',
                title: 'Soğutma Kanalı: Uniform duvar kalınlığı',
                description: 'Duvar kalınlığı uniform. Soğutma kanalı tasarımı standart mesafelerle uygulanabilir.',
                affectedFeatures: [],
                recommendation: 'Soğutma kanallarını 2x-3x duvar kalınlığı mesafede yerleştirin.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Gate Location Analysis - Yolluk yerleşimi kalite kontrolü
     * Flow path uzunluğu ve duvar kalınlığı oranı
     */
    private analyzeGateLocation(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece injection_molding için
        if (this.targetProcess !== 'injection_molding') {
            return issues;
        }

        if (!mfg.wallThickness || !brep.geometry) {
            return issues;
        }

        const avgThickness = mfg.wallThickness.avgThickness;
        const boundingBox = brep.geometry.boundingBox;

        // Approximate max flow length (bounding box diagonal)
        const maxDimension = Math.max(
            boundingBox.max.x - boundingBox.min.x,
            boundingBox.max.y - boundingBox.min.y,
            boundingBox.max.z - boundingBox.min.z
        );

        // Kural 1: Flow length to thickness ratio (L/t ratio)
        // Tipik limit: L/t < 150-200 (akış mesafesi / duvar kalınlığı)
        const flowRatio = maxDimension / avgThickness;

        if (flowRatio > 200) {
            issues.push({
                id: `GATE-${id++}`,
                severity: 'critical',
                category: 'gate_location',
                title: 'Yolluk Yerleşimi: Aşırı yüksek L/t oranı',
                description: `Flow path/thickness ratio: ${flowRatio.toFixed(0)} (max akış mesafesi ${maxDimension.toFixed(0)}mm / duvar kalınlığı ${avgThickness.toFixed(1)}mm). L/t > 200 enjeksiyonda dolum problemine neden olur.`,
                affectedFeatures: [],
                recommendation: 'Yolluk lokasyonunu merkezi konuma alın veya multiple gates kullanın. Alternatif olarak duvar kalınlığını artırın.',
                estimatedCostImpact: 30,
                relatedStandard: 'ISO 294-1',
                autofix: false,
                designSuggestion: `Duvar kalınlığını ${(maxDimension / 150).toFixed(1)}mm'ye artırın veya 2-3 gate kullanın.`
            });
        } else if (flowRatio > 150) {
            issues.push({
                id: `GATE-${id++}`,
                severity: 'warning',
                category: 'gate_location',
                title: 'Yolluk Yerleşimi: Yüksek L/t oranı',
                description: `Flow path/thickness ratio: ${flowRatio.toFixed(0)}. L/t > 150 mold tasarımında dikkat gerektirir.`,
                affectedFeatures: [],
                recommendation: 'Yolluk lokasyonunu geometrik merkeze yakın yerleştirin. Hot runner sistemi kullanarak multiple gates düşünün.',
                estimatedCostImpact: 15,
                relatedStandard: 'ISO 294-1',
                autofix: false
            });
        }

        // Kural 2: Thin sections far from gate (ince kesitler gate'den uzakta)
        const thinRegions = mfg.wallThickness.thinWallRegions || [];
        if (thinRegions.length > 0) {
            const minThickness = Math.min(...thinRegions.map(r => r.thickness));
            if (minThickness < avgThickness * 0.7) {
                issues.push({
                    id: `GATE-${id++}`,
                    severity: 'warning',
                    category: 'gate_location',
                    title: 'Yolluk Yerleşimi: İnce kesitlerde dolum riski',
                    description: `${thinRegions.length} adet ince kesit (min: ${minThickness.toFixed(1)}mm). İnce kesitler gate'den uzakta ise erken donma (premature freezing) riski vardır.`,
                    affectedFeatures: thinRegions.flatMap(r => r.faceIds),
                    recommendation: 'Yolluğu ince kesitlere yakın yerleştirin. Flow analysis ile dolum simülasyonu yapın. İnce kesitlere doğru giden akış yolunda duvar kalınlığını kademeli azaltın.',
                    estimatedCostImpact: 20,
                    relatedStandard: 'ISO 294-3',
                    autofix: false,
                    designSuggestion: `İnce kesitleri ${avgThickness.toFixed(1)}mm kalınlığa getirin veya gate lokasyonunu optimize edin.`
                });
            }
        }

        // Kural 3: Gate type recommendation (kural setinden)
        if (this.rules.gateTypes && this.rules.gateTypes.length > 0) {
            const gateInfo = `Önerilen gate tipleri: ${this.rules.gateTypes.join(', ')}.`;
            issues.push({
                id: `GATE-${id++}`,
                severity: 'info',
                category: 'gate_location',
                title: 'Yolluk Yerleşimi: Gate tipi önerisi',
                description: `${gateInfo} Part geometrisine göre en uygun gate tipini seçin.`,
                affectedFeatures: [],
                recommendation: 'Edge gate estetik olmayan yüzeylerde, submarine gate gizli yerleşim için, hot runner çoklu gate ve cycle time optimizasyonu için önerilir.',
                estimatedCostImpact: 0,
                autofix: false
            });
        }

        if (issues.filter(i => i.severity !== 'pass' && i.severity !== 'info').length === 0 && flowRatio <= 150) {
            issues.push({
                id: `GATE-${id++}`,
                severity: 'pass',
                category: 'gate_location',
                title: 'Yolluk Yerleşimi: Uygun L/t oranı',
                description: `Flow path/thickness ratio: ${flowRatio.toFixed(0)}. Yolluk yerleşimi için uygun geometri.`,
                affectedFeatures: [],
                recommendation: 'Yolluğu geometrik merkeze yakın yerleştirin.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Burr/Flash Risk Analysis - Kenar çapak riski
     * Parting line keskin kenar kesişimleri ve ince duvarlar
     */
    private analyzeBurrRisk(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // CNC ve casting süreçleri için
        if (this.targetProcess !== 'cnc_machining' &&
            this.targetProcess !== 'die_casting' &&
            this.targetProcess !== 'injection_molding' &&
            !this.targetProcess.includes('casting')) {
            return issues;
        }

        // Kural 1: Sharp edge intersections at parting line
        const sharpEdgeCount = brep.geometry?.sharpEdges || 0;
        const hasPartingLine = mfg.partingLine !== undefined;

        if (sharpEdgeCount > 5 && hasPartingLine) {
            issues.push({
                id: `BURR-${id++}`,
                severity: 'warning',
                category: 'burr_risk',
                title: 'Çapak Riski: Parting line üzerinde keskin kenarlar',
                description: `${sharpEdgeCount} adet keskin kenar. Parting line üzerindeki keskin kenarlar çapak (burr/flash) oluşumuna neden olur.`,
                affectedFeatures: [],
                recommendation: 'Parting line üzerindeki keskin kenarlara minimum R0.3mm fillet ekleyin. Alternatif olarak parting line lokasyonunu revize edin.',
                estimatedCostImpact: 15,
                relatedStandard: 'ISO 13715',
                autofix: true,
                designSuggestion: `${sharpEdgeCount} adet keskin kenara R0.3-0.5mm fillet ekleyin.`
            });
        }

        // Kural 2: Thin walls near edges (deburring difficulty)
        if (mfg.wallThickness) {
            const thinRegions = mfg.wallThickness.thinWallRegions || [];
            const minThickness = mfg.wallThickness.minThickness;

            if (thinRegions.length > 0 && minThickness < 1.5) {
                issues.push({
                    id: `BURR-${id++}`,
                    severity: 'warning',
                    category: 'burr_risk',
                    title: 'Çapak Riski: İnce duvarlarda deburring zorluğu',
                    description: `${thinRegions.length} adet ince duvar (min: ${minThickness.toFixed(1)}mm). İnce duvarlar deburring sırasında deforme olabilir.`,
                    affectedFeatures: thinRegions.flatMap(r => r.faceIds),
                    recommendation: 'İnce duvarları minimum 2mm kalınlığa çıkarın. Deburring için tumbling veya vibratory finishing kullanın (manuel deburring yerine).',
                    estimatedCostImpact: 10,
                    autofix: false,
                    designSuggestion: `İnce duvarları 2.0mm kalınlığa artırın.`
                });
            }
        }

        // Kural 3: CNC için - tool exit edges
        if (this.targetProcess === 'cnc_machining') {
            const edgeCount = brep.topology?.edgeCount || 0;
            if (edgeCount > 50) {
                issues.push({
                    id: `BURR-${id++}`,
                    severity: 'info',
                    category: 'burr_risk',
                    title: 'Çapak Riski: CNC tool exit noktaları',
                    description: `${edgeCount} adet kenar. CNC işlemede tool exit noktalarında çapak oluşur.`,
                    affectedFeatures: [],
                    recommendation: 'Deburring için vibro-finishing planlayın. Kritik yüzeylere exit chamfer ekleyin. Tool path optimizasyonu ile exit noktalarını minimize edin.',
                    estimatedCostImpact: 8,
                    autofix: false
                });
            }
        }

        // Kural 4: Die casting/injection için - mold venting
        if (this.targetProcess === 'die_casting' || this.targetProcess === 'injection_molding') {
            const deepPockets = (brep.features?.pockets || []).filter((p: any) => p.depth > 20);
            if (deepPockets.length > 0) {
                issues.push({
                    id: `BURR-${id++}`,
                    severity: 'info',
                    category: 'burr_risk',
                    title: 'Çapak Riski: Derin cepler hava tahliye kontrolü',
                    description: `${deepPockets.length} adet derin cep. Yetersiz hava tahliye (venting) flash oluşumuna neden olur.`,
                    affectedFeatures: [],
                    recommendation: 'Derin ceplere vent channels ekleyin. Parting line üzerinde vent grooves kullanın. Vent depth: 0.02-0.05mm.',
                    estimatedCostImpact: 5,
                    autofix: false
                });
            }
        }

        if (issues.filter(i => i.severity !== 'pass' && i.severity !== 'info').length === 0) {
            issues.push({
                id: `BURR-${id++}`,
                severity: 'pass',
                category: 'burr_risk',
                title: 'Çapak Riski: Düşük çapak oluşum riski',
                description: 'Geometri çapak oluşumuna elverişli değil. Standart deburring yeterli.',
                affectedFeatures: [],
                recommendation: 'Standart finishing işlemleri uygulayın.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Assembly Feasibility Analysis - Montaj uyumluluğu
     * Snap-fit, boss/hole alignment, tool access
     */
    private analyzeAssemblyFeasibility(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Tüm süreçler için geçerli

        // Kural 1: Snap-fit features (thin flexible walls)
        if (mfg.wallThickness) {
            const thinRegions = mfg.wallThickness.thinWallRegions || [];
            const flexibleWalls = thinRegions.filter(r => r.thickness < 1.5 && r.thickness > 0.5);

            if (flexibleWalls.length > 0) {
                issues.push({
                    id: `ASM-${id++}`,
                    severity: 'info',
                    category: 'assembly_feasibility',
                    title: 'Montaj: Snap-fit özelliği tespit edildi',
                    description: `${flexibleWalls.length} adet esnek duvar (0.5-1.5mm). Snap-fit tasarımı tespit edildi.`,
                    affectedFeatures: flexibleWalls.flatMap(r => r.faceIds),
                    recommendation: 'Snap-fit kollarının kalınlığı t = 0.4-0.6 x nominal olmalı. Undercut angle: 20-40°. Flex test ile doğrulama yapın. Minimum insertion force için lubricant grooves ekleyin.',
                    estimatedCostImpact: 0,
                    relatedStandard: 'ISO 20753',
                    autofix: false
                });
            }
        }

        // Kural 2: Boss/hole alignment for fasteners
        const holes = brep.features?.holes || [];
        const bosses = brep.features?.bosses || [];

        if (holes.length > 3 || bosses.length > 3) {
            const totalFeatures = holes.length + bosses.length;
            issues.push({
                id: `ASM-${id++}`,
                severity: 'info',
                category: 'assembly_feasibility',
                title: 'Montaj: Çoklu delik/patron hizalama kontrolü',
                description: `${holes.length} delik, ${bosses.length} patron tespit edildi. Montaj hizalaması kritik.`,
                affectedFeatures: [],
                recommendation: 'Boss/hole hizalama için tolerans analizi yapın (±0.1mm). Datum feature kullanın. İlk montaj için pilot pin/guide pin ekleyin. Self-tapping screws için boss ID = screw OD + 0.1mm.',
                estimatedCostImpact: 5,
                relatedStandard: 'ISO 286-1',
                autofix: false
            });
        }

        // Kural 3: Boss outer diameter vs wall thickness (standart: OD = 2.5 x screw diameter)
        if (bosses.length > 0) {
            // Boss data structure check
            const bossesWithGeometry = bosses.filter((b: any) => b.outerDiameter && b.height);
            if (bossesWithGeometry.length > 0) {
                const problematicBosses = bossesWithGeometry.filter((b: any) => {
                    const odToHeightRatio = b.outerDiameter / b.height;
                    return odToHeightRatio < 0.5; // OD çok küçük, height çok büyük
                });

                if (problematicBosses.length > 0) {
                    issues.push({
                        id: `ASM-${id++}`,
                        severity: 'warning',
                        category: 'assembly_feasibility',
                        title: 'Montaj: Boss boyutlandırma problemi',
                        description: `${problematicBosses.length} adet boss'ta OD/height oranı < 0.5. Boss çok ince veya çok uzun.`,
                        affectedFeatures: [],
                        recommendation: 'Boss outer diameter = 2.5 x screw diameter kuralını uygulayın. Boss height = 2-3 x screw diameter. Wall thickness = 0.5 x boss OD.',
                        estimatedCostImpact: 10,
                        autofix: false,
                        designSuggestion: 'Boss OD\'yi artırın veya height\'ı azaltın (OD/height ≥ 0.5).'
                    });
                }
            }
        }

        // Kural 4: Minimum access clearance for tools (hex key, screwdriver)
        const internalCorners = (brep.features as any)?.internalCorners || [];
        if (holes.length > 0 && internalCorners.length > 10) {
            issues.push({
                id: `ASM-${id++}`,
                severity: 'warning',
                category: 'assembly_feasibility',
                title: 'Montaj: Takım erişim kısıtlaması riski',
                description: `${holes.length} delik ve ${internalCorners.length} iç köşe. Dar bölgelerde vidalama takımı erişimi zorlaşabilir.`,
                affectedFeatures: [],
                recommendation: 'Her fastener için minimum tool clearance: hex key için D + 20mm, screwdriver için D + 30mm. Dar köşelerdeki delikleri taşıyın veya access hole ekleyin.',
                estimatedCostImpact: 15,
                relatedStandard: 'DIN 912',
                autofix: false
            });
        }

        // Kural 5: Threaded inserts vs molded-in threads
        const threads = mfg.threads?.threads || [];
        if (threads.length > 0 && this.targetProcess === 'injection_molding') {
            issues.push({
                id: `ASM-${id++}`,
                severity: 'info',
                category: 'assembly_feasibility',
                title: 'Montaj: Diş insert seçimi',
                description: `${threads.length} adet dişli bölge. Plastik parçalarda molded-in threads yerine threaded inserts kullanımı önerilir.`,
                affectedFeatures: [],
                recommendation: 'Yüksek torque (>2 Nm) veya tekrarlı montaj için heat-set veya ultrasonic inserts kullanın. Molded-in threads düşük torque (<1 Nm) uygulamalar için uygundur.',
                estimatedCostImpact: 12,
                autofix: false
            });
        }

        if (issues.filter(i => i.severity !== 'pass' && i.severity !== 'info').length === 0) {
            issues.push({
                id: `ASM-${id++}`,
                severity: 'pass',
                category: 'assembly_feasibility',
                title: 'Montaj: Montaj dostu tasarım',
                description: 'Geometri montaj için uygun. Snap-fit ve fastener özellikleri standartlara uygun.',
                affectedFeatures: [],
                recommendation: 'Montaj talimatlarında önerilen torque değerlerini belirtin.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Feature Spacing Analysis - Özellikler arası minimum mesafe
     * Hole-to-hole, hole-to-edge, rib-to-rib spacing
     */
    private analyzeFeatureSpacing(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        const minSpacing = this.rules.minFeatureSpacing || 2.0; // Default 2mm

        // Kural 1: Hole-to-hole distance (min 2x diameter)
        const holes = brep.features?.holes || [];
        if (holes.length > 1) {
            // Simplified hole spacing check (actual implementation would need 3D distance calculation)
            // Here we flag if there are many holes (high density risk)
            const avgHoleDiameter = holes.reduce((sum: number, h: any) => sum + (h.diameter || 0), 0) / holes.length;
            const minHoleSpacing = avgHoleDiameter * 2;

            if (holes.length > 5 && avgHoleDiameter > 0) {
                issues.push({
                    id: `SPACE-${id++}`,
                    severity: 'warning',
                    category: 'feature_spacing',
                    title: 'Özellik Mesafesi: Çoklu delik yerleşimi',
                    description: `${holes.length} adet delik (ort Ø${avgHoleDiameter.toFixed(1)}mm). Delikler arası minimum mesafe ${minHoleSpacing.toFixed(1)}mm (2x çap) olmalı.`,
                    affectedFeatures: [],
                    recommendation: `Delikler arası mesafeyi minimum ${minHoleSpacing.toFixed(1)}mm yapın. Deliklerin merkezleri arasındaki mesafe 2.5-3x çap ideal. Çok yakın delikler duvar zayıflığına neden olur.`,
                    estimatedCostImpact: 12,
                    relatedStandard: 'ISO 2768-1',
                    autofix: false,
                    designSuggestion: `Delikler arası mesafeyi ≥${minHoleSpacing.toFixed(1)}mm yapın.`
                });
            }
        }

        // Kural 2: Hole-to-edge distance (min 1x diameter)
        if (holes.length > 0 && brep.geometry) {
            const avgHoleDiameter = holes.reduce((sum: number, h: any) => sum + (h.diameter || 0), 0) / holes.length;
            const minEdgeDistance = avgHoleDiameter * 1.0;

            // Simplified: If holes exist, warn about edge distance
            issues.push({
                id: `SPACE-${id++}`,
                severity: 'info',
                category: 'feature_spacing',
                title: 'Özellik Mesafesi: Delik-kenar mesafesi kontrolü',
                description: `${holes.length} adet delik. Delik merkezinden kenara minimum mesafe ${minEdgeDistance.toFixed(1)}mm (1x çap) olmalı.`,
                affectedFeatures: [],
                recommendation: `Delik merkezini kenara minimum ${minEdgeDistance.toFixed(1)}mm mesafede tutun. Daha güvenli tasarım için 1.5x çap önerilir. Kenara çok yakın delikler çatlak riski taşır.`,
                estimatedCostImpact: 0,
                relatedStandard: 'ISO 2768-1',
                autofix: false
            });
        }

        // Kural 3: Rib-to-rib spacing (min = wall thickness)
        const ribs = brep.features?.ribs || [];
        if (ribs.length > 2) {
            const avgRibThickness = ribs.reduce((sum: number, r: any) => sum + (r.thickness || 0), 0) / ribs.length;
            const wallThickness = mfg.wallThickness?.avgThickness || 2.0;
            const minRibSpacing = Math.max(wallThickness, avgRibThickness);

            issues.push({
                id: `SPACE-${id++}`,
                severity: 'warning',
                category: 'feature_spacing',
                title: 'Özellik Mesafesi: Nervür mesafesi kontrolü',
                description: `${ribs.length} adet nervür. Nervürler arası minimum mesafe ${minRibSpacing.toFixed(1)}mm (duvar kalınlığı) olmalı.`,
                affectedFeatures: [],
                recommendation: `Nervürler arası mesafeyi minimum ${minRibSpacing.toFixed(1)}mm yapın. Çok yakın nervürler hava tuzağı ve sink mark riski yaratır. İdeal nervür mesafesi = 2-3x duvar kalınlığı.`,
                estimatedCostImpact: 10,
                relatedStandard: 'ISO 294-4',
                autofix: false,
                designSuggestion: `Nervürler arası mesafeyi ≥${minRibSpacing.toFixed(1)}mm yapın.`
            });
        }

        // Kural 4: General feature spacing (use rules.minFeatureSpacing)
        if (minSpacing > 0) {
            const totalFeatures = holes.length + ribs.length + (brep.features?.bosses?.length || 0);
            if (totalFeatures > 8) {
                issues.push({
                    id: `SPACE-${id++}`,
                    severity: 'info',
                    category: 'feature_spacing',
                    title: 'Özellik Mesafesi: Genel özellik yoğunluğu',
                    description: `${totalFeatures} adet özellik (delik+nervür+patron). Yüksek özellik yoğunluğu minimum mesafe kurallarını zorlaştırır.`,
                    affectedFeatures: [],
                    recommendation: `Tüm özellikler arası minimum mesafe ${minSpacing.toFixed(1)}mm olmalı. Özellik yoğunluğunu azaltmak için kombine features kullanın. DFM review yapın.`,
                    estimatedCostImpact: 8,
                    autofix: false
                });
            }
        }

        if (issues.filter(i => i.severity !== 'pass' && i.severity !== 'info').length === 0) {
            issues.push({
                id: `SPACE-${id++}`,
                severity: 'pass',
                category: 'feature_spacing',
                title: 'Özellik Mesafesi: Uygun özellik mesafeleri',
                description: 'Özellikler arası mesafeler üretim için yeterli.',
                affectedFeatures: [],
                recommendation: 'Mevcut özellik yerleşimini koruyun.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Shrinkage Compensation Analysis - Malzeme çekme etkisi
     * Uniform olmayan duvar kalınlığı nedeniyle diferansiyel çekme
     */
    private analyzeShrinkageCompensation(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Sadece injection_molding, die_casting, casting süreçleri için
        if (this.targetProcess !== 'injection_molding' &&
            this.targetProcess !== 'die_casting' &&
            !this.targetProcess.includes('casting')) {
            return issues;
        }

        if (!mfg.wallThickness) {
            return issues;
        }

        // Kural 1: Non-uniform wall thickness causing differential shrinkage
        const avgThickness = mfg.wallThickness.avgThickness;
        const minThickness = mfg.wallThickness.minThickness;
        const maxThickness = mfg.wallThickness.maxThickness;
        const thicknessRange = maxThickness - minThickness;

        if (thicknessRange > avgThickness * 0.5) {
            issues.push({
                id: `SHRINK-${id++}`,
                severity: 'critical',
                category: 'shrinkage_compensation',
                title: 'Çekme Kompansasyonu: Diferansiyel çekme riski',
                description: `Duvar kalınlığı varyansı ${thicknessRange.toFixed(1)}mm (min: ${minThickness.toFixed(1)}, max: ${maxThickness.toFixed(1)}, ort: ${avgThickness.toFixed(1)}mm). Kalın ve ince bölgeler farklı oranda çeker, bu warpage/dimensional error'e neden olur.`,
                affectedFeatures: [],
                recommendation: 'Duvar kalınlığını uniform tutun (±15% tolerans). Kalınlık değişimini kademeli yapın. Tolerans kritik boyutlara shrinkage compensation faktörü uygulayın (tipik: +0.3-0.8%).',
                estimatedCostImpact: 25,
                relatedStandard: 'ISO 294-4',
                autofix: false,
                designSuggestion: `Duvar kalınlığını ${avgThickness.toFixed(1)}mm ±15% (${(avgThickness * 0.85).toFixed(1)}-${(avgThickness * 1.15).toFixed(1)}mm) tolerans ile uniform yapın.`
            });
        }

        // Kural 2: Thick-to-thin transitions (max 3:1 ratio)
        const thinRegions = mfg.wallThickness.thinWallRegions || [];
        const thickRegions = mfg.wallThickness.thickWallRegions || [];

        if (thinRegions.length > 0 && thickRegions.length > 0) {
            const minThin = Math.min(...thinRegions.map(r => r.thickness));
            const maxThick = Math.max(...thickRegions.map(r => r.thickness));
            const transitionRatio = maxThick / minThin;

            if (transitionRatio > 3) {
                issues.push({
                    id: `SHRINK-${id++}`,
                    severity: 'warning',
                    category: 'shrinkage_compensation',
                    title: 'Çekme Kompansasyonu: Aşırı kalınlık geçişi',
                    description: `Kalın/ince oran: ${transitionRatio.toFixed(1)}:1 (max: ${maxThick.toFixed(1)}mm, min: ${minThin.toFixed(1)}mm). 3:1'den büyük geçişler sink mark ve warpage riski yaratır.`,
                    affectedFeatures: [...thinRegions.flatMap(r => r.faceIds), ...thickRegions.flatMap(r => r.faceIds)],
                    recommendation: 'Kalınlık geçişini kademeli yapın (max 3:1 oran). Kalın bölgelere coring uygulayın. Geçiş bölgelerine minimum 3mm uzunluğunda fillet ekleyin.',
                    estimatedCostImpact: 18,
                    relatedStandard: 'ISO 294-4',
                    autofix: false,
                    designSuggestion: `Kalın bölgeleri ${(minThin * 3).toFixed(1)}mm'nin altına indirin veya ince bölgeleri ${(maxThick / 3).toFixed(1)}mm'nin üstüne çıkarın.`
                });
            }
        }

        // Kural 3: Material-specific shrinkage rates (örnek değerler)
        const materialShrinkageRates: Record<string, number> = {
            'PP': 1.5,      // Polypropylene: 1.5-2%
            'PE': 2.0,      // Polyethylene: 2-4%
            'ABS': 0.5,     // ABS: 0.5-0.7%
            'PC': 0.6,      // Polycarbonate: 0.6-0.8%
            'PA': 1.2,      // Polyamide (Nylon): 1.0-1.5%
            'POM': 2.0,     // Acetal: 2.0-2.2%
            'Aluminum': 1.2 // Aluminum die casting: ~1.2%
        };

        // Simplified material detection (would need actual material property)
        const processType = this.targetProcess;
        let estimatedShrinkage = 1.0; // Default 1%
        let materialHint = 'plastic';

        if (processType === 'injection_molding') {
            estimatedShrinkage = 1.0; // Ortalama plastic shrinkage
            materialHint = 'plastic';
        } else if (processType === 'die_casting') {
            estimatedShrinkage = 1.2;
            materialHint = 'aluminum/zinc';
        }

        issues.push({
            id: `SHRINK-${id++}`,
            severity: 'info',
            category: 'shrinkage_compensation',
            title: 'Çekme Kompansasyonu: Malzeme çekme oranı',
            description: `Tahmini malzeme çekme oranı: ~${estimatedShrinkage}% (${materialHint}). Kritik boyutlara kompensasyon uygulanmalı.`,
            affectedFeatures: [],
            recommendation: `Kritik boyutları mold tasarımında ${estimatedShrinkage}% büyük yapın. Örnek: 100mm boyut için mold cavity ${(100 * (1 + estimatedShrinkage / 100)).toFixed(2)}mm. İlk shot sonrası boyut ölçümü yapın ve gerekirse revize edin.`,
            estimatedCostImpact: 0,
            relatedStandard: 'ISO 294-4',
            autofix: false
        });

        if (issues.filter(i => i.severity === 'critical' || i.severity === 'warning').length === 0) {
            issues.push({
                id: `SHRINK-${id++}`,
                severity: 'pass',
                category: 'shrinkage_compensation',
                title: 'Çekme Kompansasyonu: Uniform duvar kalınlığı',
                description: 'Duvar kalınlığı uniform. Diferansiyel çekme riski düşük.',
                affectedFeatures: [],
                recommendation: 'Standart shrinkage compensation faktörü uygulayın.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Residual Stress Analysis - Gerilim yoğunlaşma bölgeleri
     * Keskin köşeler, kalınlık geçişleri, yüksek aspect ratio özellikleri
     */
    private analyzeResidualStress(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Tüm süreçler için geçerli (özellikle plastik ve metal)

        // Kural 1: Sharp corners without fillets (stress risers)
        const sharpEdgeCount = brep.geometry?.sharpEdges || 0;
        const filletCount = brep.features?.fillets?.length || 0;
        const chamferCount = brep.features?.chamfers?.length || 0;
        const totalRadiusedEdges = filletCount + chamferCount;

        if (sharpEdgeCount > totalRadiusedEdges * 2) {
            const unradiusedCount = sharpEdgeCount - totalRadiusedEdges;
            issues.push({
                id: `STRESS-${id++}`,
                severity: 'warning',
                category: 'residual_stress',
                title: 'Kalıntı Gerilim: Keskin köşe gerilim yoğunlaşması',
                description: `${unradiusedCount} adet fillet/chamfer olmayan keskin köşe. Keskin köşeler stress concentration (gerilim yoğunlaşması) yaratır ve çatlak riski taşır.`,
                affectedFeatures: [],
                recommendation: `Tüm iç köşelere minimum R${this.rules.minFilletRadius}mm fillet ekleyin. Dış köşelere C0.5mm chamfer ekleyin. Özellikle yükleme noktaları ve montaj bölgelerinde kritik.`,
                estimatedCostImpact: 12,
                relatedStandard: 'ISO 13715',
                autofix: true,
                designSuggestion: `${unradiusedCount} adet keskin köşeye R${this.rules.minFilletRadius}-${this.rules.minFilletRadius * 2}mm fillet ekleyin.`
            });
        }

        // Kural 2: Thickness transitions (gradual vs abrupt)
        if (mfg.wallThickness) {
            const thinRegions = mfg.wallThickness.thinWallRegions || [];
            const thickRegions = mfg.wallThickness.thickWallRegions || [];
            const avgThickness = mfg.wallThickness.avgThickness;

            if (thinRegions.length > 0 && thickRegions.length > 0) {
                const minThin = Math.min(...thinRegions.map(r => r.thickness));
                const maxThick = Math.max(...thickRegions.map(r => r.thickness));
                const thicknessChange = maxThick - minThin;

                if (thicknessChange > avgThickness * 0.7) {
                    issues.push({
                        id: `STRESS-${id++}`,
                        severity: 'warning',
                        category: 'residual_stress',
                        title: 'Kalıntı Gerilim: Ani kalınlık değişimi',
                        description: `Kalınlık değişimi ${thicknessChange.toFixed(1)}mm (${minThin.toFixed(1)}→${maxThick.toFixed(1)}mm). Ani kalınlık değişimleri gerilim yoğunlaşması yaratır.`,
                        affectedFeatures: [...thinRegions.flatMap(r => r.faceIds), ...thickRegions.flatMap(r => r.faceIds)],
                        recommendation: 'Kalınlık geçişlerini kademeli yapın (minimum 3:1 eğim). Geçiş bölgelerinde large fillet (R ≥ kalınlık farkı) kullanın. Step geçişler yerine smooth taper tercih edin.',
                        estimatedCostImpact: 15,
                        autofix: false,
                        designSuggestion: `Kalınlık geçişlerini minimum ${(thicknessChange * 3).toFixed(0)}mm uzunluğunda gradual taper yapın.`
                    });
                }
            }
        }

        // Kural 3: Features with high aspect ratios (ribs, bosses)
        const ribs = brep.features?.ribs || [];
        const bosses = brep.features?.bosses || [];

        const highAspectRatioRibs = ribs.filter((r: any) => {
            if (!r.height || !r.thickness) return false;
            return (r.height / r.thickness) > (this.rules.maxAspectRatio || 6);
        });

        if (highAspectRatioRibs.length > 0) {
            issues.push({
                id: `STRESS-${id++}`,
                severity: 'warning',
                category: 'residual_stress',
                title: 'Kalıntı Gerilim: Yüksek aspect ratio nervürler',
                description: `${highAspectRatioRibs.length} adet nervürde height/thickness oranı >${this.rules.maxAspectRatio || 6}. Yüksek aspect ratio warpage ve stress concentration riski taşır.`,
                affectedFeatures: [],
                recommendation: `Nervür height/thickness oranını maksimum ${this.rules.maxAspectRatio || 6}:1'e indirin. Uzun nervürleri bölün veya gusset ekleyin. Nervür kalınlığını 0.5-0.6x duvar kalınlığı yapın.`,
                estimatedCostImpact: 10,
                relatedStandard: 'ISO 294-4',
                autofix: false,
                designSuggestion: `Nervür kalınlığını artırın veya yüksekliği azaltın (max aspect ratio: ${this.rules.maxAspectRatio || 6}:1).`
            });
        }

        // Kural 4: Deep holes (L/D ratio)
        const holes = brep.features?.holes || [];
        const maxLDRatio = this.rules.maxLDRatio || 10;
        const deepHoles = holes.filter((h: any) => {
            if (!h.depth || !h.diameter || h.diameter === 0) return false;
            return (h.depth / h.diameter) > maxLDRatio;
        });

        if (deepHoles.length > 0) {
            issues.push({
                id: `STRESS-${id++}`,
                severity: 'warning',
                category: 'residual_stress',
                title: 'Kalıntı Gerilim: Derin delik gerilim riski',
                description: `${deepHoles.length} adet delikte L/D oranı >${maxLDRatio}. Derin delikler drilling sırasında ve sonrasında residual stress oluşturur.`,
                affectedFeatures: [],
                recommendation: `Delik L/D oranını maksimum ${maxLDRatio}:1'e indirin. Derin delikler için gun drilling veya deep hole boring kullanın. Drilling sonrası stress relief (gerilim giderme) uygulayın.`,
                estimatedCostImpact: 12,
                relatedStandard: 'ISO 286-2',
                autofix: false,
                designSuggestion: `Delik derinliğini azaltın veya çapını artırın (max L/D: ${maxLDRatio}:1).`
            });
        }

        // Kural 5: Processes that induce residual stress (welding, casting, heat treatment)
        if (this.targetProcess === 'welding' ||
            this.targetProcess === 'heat_treatment' ||
            this.targetProcess.includes('casting')) {
            issues.push({
                id: `STRESS-${id++}`,
                severity: 'info',
                category: 'residual_stress',
                title: 'Kalıntı Gerilim: Süreç-kaynaklı gerilim',
                description: `${this.targetProcess} süreci yüksek residual stress oluşturur. Soğuma sırasında termal gradients gerilim yaratır.`,
                affectedFeatures: [],
                recommendation: 'Stress relief heat treatment uygulayın (580-650°C, 1-2 saat). Simetrik geometri ve uniform section thickness tercih edin. Kaynak için balanced weld sequence kullanın.',
                estimatedCostImpact: 18,
                relatedStandard: 'ISO 2553',
                autofix: false
            });
        }

        if (issues.filter(i => i.severity === 'critical' || i.severity === 'warning').length === 0) {
            issues.push({
                id: `STRESS-${id++}`,
                severity: 'pass',
                category: 'residual_stress',
                title: 'Kalıntı Gerilim: Düşük gerilim riski',
                description: 'Geometri stress concentration riski taşımıyor. Fillets ve gradual transitions mevcut.',
                affectedFeatures: [],
                recommendation: 'Mevcut tasarımı koruyun.',
                autofix: false
            });
        }

        return issues;
    }

    /**
     * Material-Specific Rules Analysis - Malzeme-özel tasarım kontrolleri
     * Polymer, metal, composite için özel kurallar
     */
    private analyzeMaterialSpecificRules(brep: GrupBrepResult, mfg: ManufacturingAnalysis, startId: number): DFMIssue[] {
        const issues: DFMIssue[] = [];
        let id = startId;

        // Material detection based on process and geometry hints
        let materialType: 'polymer' | 'metal' | 'composite' | 'unknown' = 'unknown';

        // Check for explicit composite hint first
        if ((brep.geometry as any)?.materialHint === 'composite') {
            materialType = 'composite';
        } else if (this.targetProcess === 'injection_molding' || this.targetProcess === '3d_printing') {
            materialType = 'polymer';
        } else if (this.targetProcess === 'die_casting' ||
                   this.targetProcess === 'cnc_machining' ||
                   this.targetProcess.includes('casting') ||
                   this.targetProcess === 'forging') {
            materialType = 'metal';
        } else if (this.targetProcess === 'sheet_metal') {
            materialType = 'metal';
        }

        // ═══════════════════════════════════════════════════════════
        // POLYMER-SPECIFIC CHECKS
        // ═══════════════════════════════════════════════════════════
        if (materialType === 'polymer') {
            // Kural 1: UV exposure surfaces (polymer degradation)
            const surfaceArea = brep.geometry?.surfaceArea || 0;
            const boundingBox = brep.geometry?.boundingBox;

            if (surfaceArea > 10000 && boundingBox) { // >100cm² exposed surface
                issues.push({
                    id: `MAT-${id++}`,
                    severity: 'info',
                    category: 'material_specific',
                    title: 'Malzeme (Polimer): UV maruziyeti riski',
                    description: `Geniş yüzey alanı (${(surfaceArea / 100).toFixed(0)}cm²). Dış ortam kullanımında UV degradation riski vardır.`,
                    affectedFeatures: [],
                    recommendation: 'UV-stabilized resin kullanın (örn: UV additives, carbon black). Alternatif olarak UV koruyucu kaplama uygulayın. Kritik yüzeyler için ASA, PC/ASA veya outdoor-grade materials seçin.',
                    estimatedCostImpact: 10,
                    relatedStandard: 'ISO 4892-2',
                    autofix: false
                });
            }

            // Kural 2: Max flow length (plastic injection)
            if (this.targetProcess === 'injection_molding' && mfg.wallThickness && boundingBox) {
                const avgThickness = mfg.wallThickness.avgThickness;
                const maxDimension = Math.max(
                    boundingBox.max.x - boundingBox.min.x,
                    boundingBox.max.y - boundingBox.min.y,
                    boundingBox.max.z - boundingBox.min.z
                );
                const maxFlowLength = avgThickness * 150; // Tipik limit: 150x duvar kalınlığı

                if (maxDimension > maxFlowLength) {
                    issues.push({
                        id: `MAT-${id++}`,
                        severity: 'warning',
                        category: 'material_specific',
                        title: 'Malzeme (Polimer): Maksimum akış uzunluğu aşımı',
                        description: `Part dimension ${maxDimension.toFixed(0)}mm > max flow length ${maxFlowLength.toFixed(0)}mm (150x duvar kalınlığı). Plastic flow premature freezing riski taşır.`,
                        affectedFeatures: [],
                        recommendation: 'Multiple gates kullanın veya duvar kalınlığını artırın. High-flow grade resin (MFI > 20) seçin. Hot runner sistemi ile gateleri optimize edin.',
                        estimatedCostImpact: 22,
                        relatedStandard: 'ISO 294-1',
                        autofix: false,
                        designSuggestion: `Duvar kalınlığını ${(maxDimension / 150).toFixed(1)}mm'ye artırın veya 2-3 gate ekleyin.`
                    });
                }
            }

            // Kural 3: Creep (uzun süreli yük altında deformasyon)
            const ribs = brep.features?.ribs || [];
            const bosses = brep.features?.bosses || [];
            if (ribs.length > 3 || bosses.length > 3) {
                issues.push({
                    id: `MAT-${id++}`,
                    severity: 'info',
                    category: 'material_specific',
                    title: 'Malzeme (Polimer): Creep riski (uzun süreli yük)',
                    description: 'Çoklu nervür/patron yapısı. Polimer malzemeler uzun süreli yük altında creep (sünme) gösterir.',
                    affectedFeatures: [],
                    recommendation: 'Yükleme bölgelerine metal insert veya fiber-reinforced grade resin kullanın. Tasarım yükünün %50\'si ile creep test yapın (1000 saat, 23°C). Glass-filled grades (GF30) creep resistance artırır.',
                    estimatedCostImpact: 8,
                    relatedStandard: 'ISO 899-1',
                    autofix: false
                });
            }
        }

        // ═══════════════════════════════════════════════════════════
        // METAL-SPECIFIC CHECKS
        // ═══════════════════════════════════════════════════════════
        if (materialType === 'metal') {
            // Kural 1: Grain direction effects (forging, rolling)
            if (this.targetProcess === 'forging' || this.targetProcess === 'sheet_metal') {
                issues.push({
                    id: `MAT-${id++}`,
                    severity: 'info',
                    category: 'material_specific',
                    title: 'Malzeme (Metal): Tane yönü etkisi',
                    description: `${this.targetProcess} süreci ile üretilen parçalarda tane yönü (grain direction) mekanik özellikleri etkiler.`,
                    affectedFeatures: [],
                    recommendation: 'Kritik yüklemeleri tane yönü ile paralel yapın. Parting line/forging direction kritik yüzeylere dik olmalı. Çekme yüklemesi için longitudinal direction, kesme için transverse direction tercih edin.',
                    estimatedCostImpact: 0,
                    relatedStandard: 'ASTM E112',
                    autofix: false
                });
            }

            // Kural 2: Minimum section for heat treatment (hardening depth)
            if (mfg.wallThickness) {
                const minThickness = mfg.wallThickness.minThickness;
                const avgThickness = mfg.wallThickness.avgThickness;

                if (minThickness < 3.0 && this.targetProcess !== '3d_printing') {
                    issues.push({
                        id: `MAT-${id++}`,
                        severity: 'warning',
                        category: 'material_specific',
                        title: 'Malzeme (Metal): İnce kesit ısıl işlem zorluğu',
                        description: `Minimum kesit ${minThickness.toFixed(1)}mm. İnce kesitler (<3mm) ısıl işlem sırasında aşırı hızlı soğur ve çarpılma/çatlama riski taşır.`,
                        affectedFeatures: [],
                        recommendation: 'İnce kesitleri minimum 3mm yapın. Alternatif olarak surface hardening (case hardening, nitriding) kullanın. Through-hardening için minimum 5mm kesit gerekir.',
                        estimatedCostImpact: 15,
                        relatedStandard: 'ISO 6336-5',
                        autofix: false,
                        designSuggestion: `İnce kesitleri 3.0mm minimum kalınlığa çıkarın.`
                    });
                }
            }

            // Kural 3: Machining allowance for castings
            if (this.targetProcess.includes('casting')) {
                const surfaceArea = brep.geometry?.surfaceArea || 0;
                if (surfaceArea > 5000) {
                    issues.push({
                        id: `MAT-${id++}`,
                        severity: 'info',
                        category: 'material_specific',
                        title: 'Malzeme (Metal): Döküm işleme payı',
                        description: `Döküm parça (${(surfaceArea / 100).toFixed(0)}cm²). Tolerance kritik yüzeylere machining allowance gerekir.`,
                        affectedFeatures: [],
                        recommendation: 'Kritik yüzeylere 1.5-3mm machining allowance bırakın. As-cast yüzey toleransı: ±0.5mm (sand casting), ±0.2mm (die casting). Hassas boyutlar için post-machining planlayın.',
                        estimatedCostImpact: 12,
                        relatedStandard: 'ISO 8062',
                        autofix: false
                    });
                }
            }

            // Kural 4: Corrosion protection
            const internalCorners = (brep.features as any)?.internalCorners || [];
            if (internalCorners.length > 5) {
                issues.push({
                    id: `MAT-${id++}`,
                    severity: 'info',
                    category: 'material_specific',
                    title: 'Malzeme (Metal): Korozyon riski - iç köşeler',
                    description: `${internalCorners.length} adet iç köşe. Keskin iç köşeler korozyon başlangıç noktası olur (crevice corrosion).`,
                    affectedFeatures: [],
                    recommendation: 'İç köşelere minimum R1mm fillet ekleyin. Korozif ortam için surface treatment (anodizing, plating, powder coating) uygulayın. Paslanmaz çelik veya aluminum alloy seçin.',
                    estimatedCostImpact: 8,
                    relatedStandard: 'ISO 8044',
                    autofix: true,
                    designSuggestion: `${internalCorners.length} adet iç köşeye R1.0-2.0mm fillet ekleyin.`
                });
            }
        }

        // ═══════════════════════════════════════════════════════════
        // COMPOSITE-SPECIFIC CHECKS
        // ═══════════════════════════════════════════════════════════
        // Note: Composite material detection requires explicit material property
        // These checks are kept for future material database integration
        if (materialType === 'composite' || (brep.geometry as any)?.materialHint === 'composite') {
            // Kural 1: Fiber orientation considerations
            issues.push({
                id: `MAT-${id++}`,
                severity: 'info',
                category: 'material_specific',
                title: 'Malzeme (Kompozit): Fiber oryantasyonu',
                description: 'Kompozit malzemeler fiber yönüne göre anizotropik özellik gösterir.',
                affectedFeatures: [],
                recommendation: 'Ana yüklemeleri fiber yönü ile align edin. ±45° lay-up kesme yükleri için, 0°/90° tensile yükleri için. Minimum 3-ply thickness kullanın. Delamination riski için edge sealing uygulayın.',
                estimatedCostImpact: 0,
                relatedStandard: 'ISO 527-4',
                autofix: false
            });

            // Kural 2: Hole drilling - delamination risk
            const holes = brep.features?.holes || [];
            if (holes.length > 0) {
                issues.push({
                    id: `MAT-${id++}`,
                    severity: 'warning',
                    category: 'material_specific',
                    title: 'Malzeme (Kompozit): Delme delaminasyon riski',
                    description: `${holes.length} adet delik. Kompozit malzemede delme sırasında delamination (tabaka ayrılması) riski vardır.`,
                    affectedFeatures: [],
                    recommendation: 'Brad-point drill veya diamond-coated tooling kullanın. Düşük feed rate (0.05-0.1 mm/rev) uygulayın. Backup plate ile delme yapın. Delikleri mümkünse molding sırasında bırakın.',
                    estimatedCostImpact: 15,
                    relatedStandard: 'ASTM D5961',
                    autofix: false
                });
            }
        }

        // ═══════════════════════════════════════════════════════════
        // GENERAL MATERIAL NOTE
        // ═══════════════════════════════════════════════════════════
        if (materialType === 'unknown') {
            issues.push({
                id: `MAT-${id++}`,
                severity: 'info',
                category: 'material_specific',
                title: 'Malzeme: Malzeme özelliği belirtilmemiş',
                description: 'DFM analizi için malzeme özelliği belirtilmemiş. Daha detaylı analiz için malzeme tipini ekleyin.',
                affectedFeatures: [],
                recommendation: 'Material database\'den uygun malzeme seçin. Malzeme seçimi mekanik özellikler, işlenebilirlik ve maliyet açısından kritiktir.',
                estimatedCostImpact: 0,
                autofix: false
            });
        }

        if (issues.filter(i => i.severity !== 'pass' && i.severity !== 'info').length === 0 && materialType !== 'unknown') {
            issues.push({
                id: `MAT-${id++}`,
                severity: 'pass',
                category: 'material_specific',
                title: 'Malzeme: Malzeme-özel kurallar uygun',
                description: `${materialType} malzeme için tasarım kuralları uygun.`,
                affectedFeatures: [],
                recommendation: 'Mevcut tasarımı koruyun.',
                autofix: false
            });
        }

        return issues;
    }
}

// Singleton instance
let dfmAnalyzerInstance: FikoDFMAnalyzer | null = null;

export function getDFMAnalyzer(): FikoDFMAnalyzer {
    if (!dfmAnalyzerInstance) {
        dfmAnalyzerInstance = new FikoDFMAnalyzer();
    }
    return dfmAnalyzerInstance;
}

export function disposeDFMAnalyzer(): void {
    dfmAnalyzerInstance = null;
}
